(() => {
  // personalisation-product-integration/gift-message.mjs
  function normaliseGift(input) {
    if (input == null || input.enabled === false) return null;
    if (typeof input !== "object" || input.enabled !== true) throw Error("Choose whether to add a gift message.");
    const text = (key, max, label) => {
      if (typeof input[key] !== "string") throw Error(`Enter ${label}.`);
      const value = input[key].normalize("NFC").trim();
      if (!value || value.length > max || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value)) throw Error(`Enter ${label} using up to ${max} characters.`);
      return value;
    };
    const recipientName = text("recipientName", 80, "the recipient's name"), fromName = text("fromName", 80, "your name"), message = text("message", 300, "a gift message");
    const recipientEmail = text("recipientEmail", 254, "the recipient's email address");
    if (/[\r\n\t]/.test(recipientName + fromName)) throw Error("Enter names on one line.");
    if (!/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(recipientEmail)) throw Error("Enter a valid recipient email address.");
    return { enabled: true, recipientName, recipientEmail, fromName, message, delivery: "after-confirmed-delivery" };
  }
  function giftOrderProperties(input) {
    const gift = normaliseGift(input);
    return gift ? { "Gift recipient": gift.recipientName, "Gift from": gift.fromName, "Gift message": gift.message, "Gift delivery": "Email after courier-confirmed delivery", _Laboo_Is_Gift: "true", _Laboo_Gift_Recipient_Email: gift.recipientEmail, _Laboo_Gift_Email_Status: "pending_order" } : {};
  }

  // personalisation-product-integration/paper-projection.mjs
  function inverseProjection(quad) {
    const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;
    const dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3, dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3;
    const den = dx1 * dy2 - dx2 * dy1, g = (dx3 * dy2 - dx2 * dy3) / den, h = (dx1 * dy3 - dx3 * dy1) / den;
    const a = x1 - x0 + g * x1, b = x3 - x0 + h * x3, c = x0, d = y1 - y0 + g * y1, e = y3 - y0 + h * y3, f = y0;
    return [e - f * h, c * h - b, b * f - c * e, f * g - d, a - c * g, c * d - a * f, d * h - e * g, b * g - a * h, a * e - b * d];
  }
  function compositePaper(source, background, quad, options = {}) {
    const { width, height, data } = background, result = new Uint8ClampedArray(data), s = source.data, sw = source.width, sh = source.height;
    const m = inverseProjection(quad), minX = Math.max(0, Math.floor(Math.min(...quad.map((p) => p[0])))), maxX = Math.min(width - 1, Math.ceil(Math.max(...quad.map((p) => p[0])))), minY = Math.max(0, Math.floor(Math.min(...quad.map((p) => p[1])))), maxY = Math.min(height - 1, Math.ceil(Math.max(...quad.map((p) => p[1]))));
    const paperW = Math.hypot(quad[1][0] - quad[0][0], quad[1][1] - quad[0][1]), paperH = Math.hypot(quad[3][0] - quad[0][0], quad[3][1] - quad[0][1]);
    for (let y = minY; y <= maxY; y++) for (let x = minX; x <= maxX; x++) {
      const X = x + 0.5, Y = y + 0.5, z = m[6] * X + m[7] * Y + m[8], u = (m[0] * X + m[1] * Y + m[2]) / z, v = (m[3] * X + m[4] * Y + m[5]) / z;
      if (u < 0 || u > 1 || v < 0 || v > 1) continue;
      const px = u * (sw - 1), py = v * (sh - 1), sx = Math.floor(px), sy = Math.floor(py), fx = px - sx, fy = py - sy;
      const p = (sy * sw + sx) * 4, q = (sy * sw + Math.min(sx + 1, sw - 1)) * 4, r = (Math.min(sy + 1, sh - 1) * sw + sx) * 4, t = (Math.min(sy + 1, sh - 1) * sw + Math.min(sx + 1, sw - 1)) * 4;
      const w0 = (1 - fx) * (1 - fy), w1 = fx * (1 - fy), w2 = (1 - fx) * fy, w3 = fx * fy;
      const coverage = Math.min(1, u * paperW + 0.5, (1 - u) * paperW + 0.5, v * paperH + 0.5, (1 - v) * paperH + 0.5), alpha = coverage * (s[p + 3] * w0 + s[q + 3] * w1 + s[r + 3] * w2 + s[t + 3] * w3) / 255, offset = (y * width + x) * 4;
      let glaze = 0;
      if (options.glazing) {
        const g = options.glazing, gx = Math.round(u * (g.width - 1)), gy = Math.round(v * (g.height - 1));
        glaze = g.data[(gy * g.width + gx) * 4 + 3] / 255;
      }
      const reflection = Math.min(0.4, glaze + (options.reflection ? options.reflection * Math.max(0, 1 - u / 0.38) : 0));
      for (let c = 0; c < 3; c++) {
        const ink = s[p + c] * w0 + s[q + c] * w1 + s[r + c] * w2 + s[t + c] * w3;
        const tint = options.tint?.[c] ?? 255;
        let foreground = options.replace ? ink * tint / 255 * (1 - reflection) + 255 * reflection : data[offset + c] * ink / 255;
        if (options.reflectionPlate) {
          const plate = options.reflectionPlate, gx = Math.round(u * (plate.width - 1)), gy = Math.round(v * (plate.height - 1)), value = plate.data[(gy * plate.width + gx) * 4 + c], base = plate.base?.[c] ?? 128;
          const strength = plate.strength ?? 1, light = Math.min(plate.maxLight ?? 1, Math.max(0, (value - base) / (255 - base)) * strength), dark = Math.min(plate.maxDark ?? 1, Math.max(0, (base - value) / base) * strength);
          foreground = value >= base ? foreground + (255 - foreground) * light : foreground * (1 - dark);
        }
        result[offset + c] = data[offset + c] * (1 - alpha) + alpha * foreground;
      }
    }
    return result;
  }

  // personalisation-product-integration/paper-mockup.mjs
  var corners = [[706.9908606, 384.1743302], [2290, 390], [2284.6211201, 2607.0842064], [708.2287463, 2615.4956493]];
  function image(url, cors = false) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (cors) img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(Error("Paper preview could not load. Please refresh."));
      img.src = url;
    });
  }
  function createPaperMockup(canvas, url, status, { size = 1200, isActive = () => canvas.closest("product-page")?.classList.contains("laboo-show-paper"), onRender = () => {
  } } = {}) {
    status ||= { textContent: "" };
    let generation = 0;
    let background;
    return async function update(artUrl, name, number2) {
      const current = ++generation;
      canvas.setAttribute("aria-busy", "true");
      status.textContent = "Preparing your paper preview\u2026";
      try {
        background ||= image(url, true).catch((e) => {
          background = null;
          throw e;
        });
        const [bg, art] = await Promise.all([background, image(artUrl)]);
        if (current !== generation) return;
        if (!isActive()) return;
        const ratio = art.naturalWidth / art.naturalHeight / (2121 / 3e3), width = Math.round(size * ratio), height = size, scale = size / 3e3;
        const layer = document.createElement("canvas");
        layer.width = art.naturalWidth;
        layer.height = art.naturalHeight;
        const ctx = layer.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(art, 0, 0);
        const source = ctx.getImageData(0, 0, layer.width, layer.height), quad = corners.map(([x, y]) => [x * scale * ratio, y * scale]);
        canvas.width = width;
        canvas.height = height;
        const output = canvas.getContext("2d", { willReadFrequently: true });
        const backdrop = getComputedStyle(document.querySelector(".laboo-original-art-stage")).backgroundColor;
        output.fillStyle = backdrop;
        output.fillRect(0, 0, width, height);
        output.save();
        output.shadowColor = "rgba(0,0,0,.24)";
        output.shadowBlur = 15 * size / 1200;
        output.shadowOffsetX = -5 * size / 1200;
        output.shadowOffsetY = 7 * size / 1200;
        output.drawImage(bg, 0, 0, width, height);
        output.restore();
        const photo = output.getImageData(0, 0, width, height);
        output.putImageData(new ImageData(compositePaper(source, photo, quad), width, height), 0, 0);
        const pad = 40 * size / 1200, left = Math.max(0, Math.floor(Math.min(...quad.map((p) => p[0])) - pad)), top = Math.max(0, Math.floor(Math.min(...quad.map((p) => p[1])) - pad));
        const right = Math.min(width, Math.ceil(Math.max(...quad.map((p) => p[0])) + pad)), bottom = Math.min(height, Math.ceil(Math.max(...quad.map((p) => p[1])) + pad));
        const cropped = document.createElement("canvas");
        cropped.width = right - left;
        cropped.height = bottom - top;
        cropped.getContext("2d").drawImage(canvas, left, top, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);
        canvas.width = cropped.width;
        canvas.height = cropped.height;
        canvas.getContext("2d").drawImage(cropped, 0, 0);
        canvas.dataset.renderer = "continuous-perspective-v2";
        canvas.dataset.background = backdrop;
        canvas.setAttribute("aria-label", `${name.toUpperCase()} / ${number2}, your personalised print on unframed paper`);
        canvas.setAttribute("aria-busy", "false");
        status.textContent = "";
        onRender();
      } catch (e) {
        if (current === generation) {
          canvas.setAttribute("aria-busy", "false");
          status.textContent = e.message;
        }
      }
    };
  }

  // personalisation-product-integration/swipe-gallery.mjs
  function attachSwipeGallery(surface, onSwipe) {
    let gesture = null, suppressClickUntil = 0;
    const ignored = "[data-gallery-index],[data-gallery-step],input,select,textarea,a,[data-swipe-ignore]";
    surface.style.touchAction = "pan-y pinch-zoom";
    surface.addEventListener("pointerdown", (e) => {
      if (e.isPrimary === false || e.button > 0 || e.target.closest?.(ignored)) return;
      gesture = { id: e.pointerId, x: e.clientX, y: e.clientY, horizontal: false };
    });
    surface.addEventListener("pointermove", (e) => {
      if (!gesture || e.pointerId !== gesture.id) return;
      const x = e.clientX - gesture.x, y = e.clientY - gesture.y;
      if (!gesture.horizontal) {
        if (Math.abs(y) > 12 && Math.abs(y) > Math.abs(x)) {
          gesture = null;
          return;
        }
        if (Math.abs(x) < 12 || Math.abs(x) < Math.abs(y) * 1.2) return;
        gesture.horizontal = true;
        try {
          surface.setPointerCapture(e.pointerId);
        } catch {
        }
      }
      if (e.cancelable) e.preventDefault();
    });
    surface.addEventListener("pointerup", (e) => {
      if (!gesture || e.pointerId !== gesture.id) return;
      const g = gesture;
      gesture = null;
      if (g.horizontal) {
        suppressClickUntil = Date.now() + 500;
        const distance = e.clientX - g.x, threshold = Math.min(70, Math.max(35, surface.clientWidth * 0.12));
        if (Math.abs(distance) >= threshold) onSwipe(distance < 0 ? 1 : -1);
      }
    });
    surface.addEventListener("pointercancel", () => {
      if (gesture?.horizontal) suppressClickUntil = Date.now() + 500;
      gesture = null;
    });
    surface.addEventListener("click", (e) => {
      if (Date.now() < suppressClickUntil) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true);
    surface.addEventListener("dragstart", (e) => {
      if (!e.target.closest?.(ignored)) e.preventDefault();
    });
  }

  // personalisation-product-integration/room-gallery-renderer.mjs
  function image2(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(Error("This room could not load. Select it again to retry."));
      img.src = url;
    });
  }
  var idle = () => new Promise((resolve) => {
    if ("requestIdleCallback" in window) window.requestIdleCallback(resolve, { timeout: 700 });
    else setTimeout(resolve, 32);
  });
  function artwork(art) {
    const canvas = document.createElement("canvas");
    canvas.width = 900;
    canvas.height = 1200;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(art, 0, 0, 900, 1200);
    return ctx.getImageData(0, 0, 900, 1200);
  }
  function paint(canvas, photo, source, scene, size, glazing, frameOverlay) {
    canvas.width = size;
    canvas.height = size;
    const output = canvas.getContext("2d", { willReadFrequently: true });
    output.drawImage(photo, 0, 0, size, size);
    if (frameOverlay) output.drawImage(frameOverlay, 0, 0, size, size);
    const background = output.getImageData(0, 0, size, size), corners2 = scene.corners.map(([x, y]) => [x * size / 1200, y * size / 1200]);
    output.putImageData(new ImageData(compositePaper(source, background, corners2, { replace: true, tint: scene.tint, glazing }), size, size), 0, 0);
  }
  function createRoomMockups(canvas, status, caption, { onRender = () => {
  }, rooms } = {}) {
    let generation = 0, artCache, thumbGeneration = 0, thumbKey = "";
    const backgrounds = /* @__PURE__ */ new Map(), thumbBackgrounds = /* @__PURE__ */ new Map(), lightMaps = /* @__PURE__ */ new Map(), frameOverlays = /* @__PURE__ */ new Map(), thumbDesigns = [];
    function frameOverlay(scene, finish) {
      const url = scene.finishOverlayUrls?.[finish] || scene.frameOverlayUrl;
      if (!url) return Promise.resolve(null);
      if (!frameOverlays.has(url)) frameOverlays.set(url, image2(url).catch((e) => {
        frameOverlays.delete(url);
        throw e;
      }));
      return frameOverlays.get(url);
    }
    const thumbnails = [...document.querySelectorAll("[data-room-thumbnail]")];
    const active = () => canvas.closest("product-page")?.classList.contains("laboo-show-room-examples");
    Object.assign(canvas.dataset, { loadedScenes: "0", loadedThumbnailScenes: "0", thumbnailsReady: "0" });
    function lighting(scene) {
      if (!lightMaps.has(scene.id)) lightMaps.set(scene.id, image2(scene.glassUrl).then((img) => {
        const surface = document.createElement("canvas");
        surface.width = img.naturalWidth;
        surface.height = img.naturalHeight;
        const ctx = surface.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        return ctx.getImageData(0, 0, surface.width, surface.height);
      }).catch((e) => {
        lightMaps.delete(scene.id);
        throw e;
      }));
      return lightMaps.get(scene.id);
    }
    function background(scene, small = false) {
      if (small && backgrounds.has(scene.id)) return backgrounds.get(scene.id);
      const cache = small ? thumbBackgrounds : backgrounds;
      if (!cache.has(scene.id)) {
        const url = new URL(scene.url);
        if (small) url.searchParams.set("width", "240");
        cache.set(scene.id, image2(url.href).catch((e) => {
          cache.delete(scene.id);
          throw e;
        }));
      }
      return cache.get(scene.id);
    }
    function scheduleThumbnails(key, source, name, number2, finish) {
      if (thumbKey === key) return;
      thumbKey = key;
      const current = ++thumbGeneration;
      thumbnails.forEach((thumb, i) => {
        if (thumbDesigns[i] !== key) thumb.setAttribute("aria-busy", "true");
      });
      (async () => {
        for (let i = 0; i < rooms.length; i++) {
          await idle();
          if (current !== thumbGeneration) return;
          if (!canvas.isConnected) return;
          if (thumbDesigns[i] === key) continue;
          const thumb = thumbnails[i];
          if (!thumb) continue;
          try {
            const scene = rooms[i], unframed = scene.finishOverlayUrls && finish === "none";
            const [photo, glazing, frame] = await Promise.all([background(scene, true), unframed ? null : lighting(scene), frameOverlay(scene, finish)]);
            if (current !== thumbGeneration) return;
            paint(thumb, photo, source, rooms[i], 240, glazing, frame);
            thumbDesigns[i] = key;
            thumb.setAttribute("aria-label", `${name.toUpperCase()} / ${number2}, ${rooms[i].label.toLowerCase()} thumbnail`);
            thumb.setAttribute("aria-busy", "false");
            thumb.dataset.personalisation = `${name.toUpperCase()} / ${number2}`;
          } catch {
            thumb.setAttribute("aria-busy", "false");
            thumb.setAttribute("aria-label", `${rooms[i].label} thumbnail unavailable`);
            thumbKey = "";
          }
          canvas.dataset.loadedThumbnailScenes = String(thumbBackgrounds.size);
          canvas.dataset.thumbnailsReady = String(thumbDesigns.filter((x) => x === key).length);
        }
      })();
    }
    function sourceFor(artUrl) {
      if (artCache?.url !== artUrl) artCache = { url: artUrl, promise: image2(artUrl).then(artwork) };
      return artCache.promise;
    }
    async function update(index, artUrl, name, number2, finish) {
      const scene = rooms[index];
      if (!scene) return;
      const current = ++generation;
      canvas.setAttribute("aria-busy", "true");
      status.textContent = `Preparing the ${scene.label.toLowerCase()} preview\u2026`;
      const started = performance.now();
      try {
        const unframed = scene.finishOverlayUrls && finish === "none";
        const [photo, source, glazing, frame] = await Promise.all([background(scene), sourceFor(artUrl), unframed ? null : lighting(scene), frameOverlay(scene, finish)]);
        if (current !== generation || !active()) return;
        const renderStarted = performance.now();
        paint(canvas, photo, source, scene, 1200, glazing, frame);
        Object.assign(canvas.dataset, { loadedScenes: String(backgrounds.size), renderMs: (performance.now() - renderStarted).toFixed(1), totalMs: (performance.now() - started).toFixed(1), scene: scene.id, renderer: "continuous-perspective-v2", fit: "stretch-to-frame", glass: "photo-matched-" + scene.id });
        if (scene.finishOverlayUrls) canvas.dataset.frameFinish = finish;
        canvas.setAttribute("aria-label", `${name.toUpperCase()} / ${number2} in the ${scene.label.toLowerCase()}${scene.finishOverlayUrls ? " \xB7 " + (finish === "none" ? "Print only" : finish.replaceAll("-", " ") + " frame") : ""}`);
        canvas.setAttribute("aria-busy", "false");
        caption.textContent = `${scene.label} \xB7 ${name.toUpperCase()} / ${number2}`;
        status.textContent = "";
        onRender();
        scheduleThumbnails(artUrl + "|" + (finish || ""), source, name, number2, finish);
      } catch (e) {
        if (current === generation) {
          canvas.setAttribute("aria-busy", "false");
          status.textContent = e.message;
        }
      }
    }
    update.prepareThumbnails = async (artUrl, name, number2, finish) => {
      try {
        const source = await sourceFor(artUrl);
        if (artCache?.url === artUrl) scheduleThumbnails(artUrl + "|" + (finish || ""), source, name, number2, finish);
      } catch {
      }
    };
    return update;
  }

  // personalisation-product-integration/gift-message-ui.mjs
  function createGiftMessage(host, { productId, api, onChange = () => {
  }, onAvailability = () => {
  } } = {}) {
    const section = document.createElement("section");
    section.className = "laboo-gift-section";
    section.hidden = true;
    section.innerHTML = '<div data-gift-choice><p class="laboo-gift-packaging-note"><strong>No invoice, receipt or pricing is included in the parcel.</strong></p><label class="laboo-gift-toggle"><input type="checkbox" data-gift-enabled> Add a gift message <small>Optional</small></label><p class="laboo-gift-delivery-note">We\u2019ll email your note to the recipient after the courier confirms delivery.</p></div><div class="laboo-gift-fields" hidden><div data-gift-part="recipientName"><label for="laboo-gift-to">Recipient\u2019s name</label><input id="laboo-gift-to" data-gift="recipientName" maxlength="80" autocomplete="off"></div><div data-gift-part="recipientEmail"><label for="laboo-gift-email">Recipient\u2019s email</label><input id="laboo-gift-email" data-gift="recipientEmail" type="email" maxlength="254" autocomplete="off" inputmode="email"><p>We\u2019ll use this address only to send your gift message after confirmed delivery.</p></div><div data-gift-part="fromName"><label for="laboo-gift-from">From</label><input id="laboo-gift-from" data-gift="fromName" maxlength="80" autocomplete="off"></div><div data-gift-part="message"><label for="laboo-gift-message">Your gift message</label><textarea id="laboo-gift-message" data-gift="message" maxlength="300" rows="3" placeholder="Write a personal note"></textarea><p><span data-gift-count>0</span>/300 characters</p></div></div><p data-gift-error role="status" aria-live="polite"></p>';
    host.append(section);
    const toggle = section.querySelector("[data-gift-enabled]"), fields = section.querySelector(".laboo-gift-fields"), error = section.querySelector("[data-gift-error]");
    let enabled = false;
    const inputs = [...fields.querySelectorAll("[data-gift]")];
    function getGift() {
      return normaliseGift(enabled && toggle.checked ? { enabled: true, ...Object.fromEntries(inputs.map((el) => [el.dataset.gift, el.value])) } : null);
    }
    function validate(field) {
      error.textContent = "";
      for (const el of inputs) el.removeAttribute("aria-invalid");
      if (!enabled || !toggle.checked) return true;
      if (field) {
        const el = inputs.find((el2) => el2.dataset.gift === field), value = el?.value.trim();
        if (value && (field !== "recipientEmail" || /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(value)) && (!["recipientName", "fromName"].includes(field) || !/[\r\n\t]/.test(value))) return true;
        error.textContent = field === "recipientEmail" ? "Enter a valid recipient email address." : "Please complete this field.";
        el?.setAttribute("aria-invalid", "true");
        el?.focus({ preventScroll: true });
        return false;
      }
      try {
        getGift();
        return true;
      } catch (e) {
        error.textContent = e.message;
        const el = inputs.find((el2) => !el2.value.trim() || el2.type === "email" && !el2.validity.valid);
        el?.setAttribute("aria-invalid", "true");
        el?.focus({ preventScroll: true });
        return false;
      }
    }
    function changed() {
      fields.hidden = !toggle.checked;
      section.querySelector("[data-gift-count]").textContent = String(section.querySelector('[data-gift="message"]').value.length);
      error.textContent = "";
      for (const el of inputs) el.removeAttribute("aria-invalid");
      onChange();
    }
    toggle.addEventListener("change", changed);
    fields.addEventListener("input", changed);
    const ready = (async () => {
      try {
        const response = await fetch(new URL("/shopify/gift-config?productId=" + encodeURIComponent(productId), api), { credentials: "omit" });
        if (!response.ok) return false;
        const config = await response.json();
        enabled = config.enabled === true;
        section.hidden = !enabled;
        onAvailability();
        return enabled;
      } catch {
        return false;
      }
    })();
    return { section, choice: section.querySelector("[data-gift-choice]"), fields, inputs, ready, getGift, getGiftKey: () => enabled && toggle.checked ? JSON.stringify(inputs.map((el) => [el.dataset.gift, el.value])) : null, validate, isEnabled: () => enabled, isSelected: () => enabled && toggle.checked };
  }

  // personalisation-product-integration/print-catalogue.mjs
  var PRINT_COUNTRIES = ["AU", "AT", "BE", "BG", "CA", "HR", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "ID", "IE", "IL", "IT", "JP", "LV", "LT", "LU", "MY", "MT", "NL", "NZ", "NO", "PL", "PT", "SA", "SG", "SK", "SI", "KR", "ES", "SE", "CH", "TW", "TH", "TR", "AE", "GB", "US"];
  var PRINT_PRODUCTS = { "210x297": { "metric": { "mm": [210, 297], "productUid": "flat_a4-8x12-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [203.2, 304.8], "productUid": "flat_8x12-inch-a4_200-gsm-80lb-uncoated_4-0_ver" } }, "297x420": { "metric": { "mm": [297, 420], "productUid": "flat_a3_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [304.8, 406.4], "productUid": "flat_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "420x594": { "metric": { "mm": [420, 594], "productUid": "flat_a2_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [406.4, 609.6], "productUid": "flat_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "594x841": { "metric": { "mm": [594, 841], "productUid": "flat_a1_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [609.6, 812.8], "productUid": "flat_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "841x1189": { "metric": { "mm": [841, 1189], "productUid": "flat_a0_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": null }, "130x180": { "metric": { "mm": [130, 180], "productUid": "flat_130x180-mm-5r_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [127, 177.8], "productUid": "flat_5r-130x180-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "270x350": { "metric": { "mm": [270, 350], "productUid": "flat_270x350-mm-11x14-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [279.4, 355.6], "productUid": "flat_11x14-inch-270x350-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "300x400": { "metric": { "mm": [300, 400], "productUid": "flat_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [304.8, 406.4], "productUid": "flat_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "400x500": { "metric": { "mm": [400, 500], "productUid": "flat_400x500-mm-16x20-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [406.4, 508], "productUid": "flat_16x20-inch-400x500-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "400x600": { "metric": { "mm": [400, 600], "productUid": "flat_400x600-mm-16x24-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [406.4, 609.6], "productUid": "flat_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "500x700": { "metric": { "mm": [500, 700], "productUid": "flat_500x700-mm-20x28-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [508, 711.2], "productUid": "flat_20x28-inch-500x700-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "600x800": { "metric": { "mm": [600, 800], "productUid": "flat_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [609.6, 812.8], "productUid": "flat_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "600x900": { "metric": { "mm": [600, 900], "productUid": "flat_600x900-mm-24x36-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [609.6, 914.4], "productUid": "flat_24x36-inch-600x900-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "700x1000": { "metric": { "mm": [700, 1e3], "productUid": "flat_700x1000-mm-28x40-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [711.2, 1016], "productUid": "flat_28x40-inch-700x1000-mm_200-gsm-80lb-uncoated_4-0_ver" } }, "750x1000": { "metric": { "mm": [750, 1e3], "productUid": "flat_750x1000-mm-30x40-inch_200-gsm-80lb-uncoated_4-0_ver" }, "northAmerica": { "mm": [762, 1016], "productUid": "flat_30x40-inch-750x1000-mm_200-gsm-80lb-uncoated_4-0_ver" } } };
  function printProduct(variant, country = "GB") {
    if (!PRINT_COUNTRIES.includes(country)) throw Error("DELIVERY_COUNTRY_NOT_SUPPORTED");
    const key = variant.mm.map((n) => n === 595 ? 594 : n).join("x"), entry = PRINT_PRODUCTS[key];
    if (!entry) throw Error("PRINT_SIZE_NOT_MAPPED");
    const product = ["US", "CA"].includes(country) ? entry.northAmerica : entry.metric;
    if (!product) return { available: false, label: "This size is unavailable for this delivery country." };
    const units = country === "US" ? "in" : "cm", divisor = country === "US" ? 25.4 : 10;
    const format = (n) => String(Number((n / divisor).toFixed(2)));
    return { ...product, available: true, country, label: product.mm.map(format).join(" \xD7 ") + " " + units };
  }

  // personalisation-product-integration/selected-size-guide.mjs
  var PERSON_MM = 1778;
  var number = (value) => String(Number(value.toFixed(2)));
  function selectedSize(variantId, country, regionalLabel = "", variants = {}) {
    const variant = variants[variantId];
    if (!variant) return null;
    const us = country === "US", product = printProduct(variant, country);
    if (!product.available) return null;
    const dimensions = product.mm.map((mm) => mm / (us ? 25.4 : 10));
    const [width, height] = dimensions, unit = us ? "in" : "cm", series = !us ? variant.originalTitle.match(/^A[0-4]\b/)?.[0] : null;
    return { label: `${series && !["US", "CA"].includes(country) ? series + " \xB7 " : ""}${number(width)} \xD7 ${number(height)} ${unit}`, widthText: `${number(width)} ${unit}`, heightText: `${number(height)} ${unit}`, mm: product.mm, personLabel: us ? "Person: 5 ft 10 in" : "Person: 178 cm", country, unit };
  }
  function createSelectedSizeGuide(container) {
    container.innerHTML = `<h3>See the size</h3><svg viewBox="0 0 2400 2050" role="img" aria-label="Selected print compared with a person" data-selected-size-guide>
    <title data-guide-title>Selected print size comparison</title>
    <line x1="100" y1="1850" x2="2260" y2="1850" stroke="#d9d5cf" stroke-width="7"/>
    <image href="https://berahgewesjy.shop/cdn/shop/files/Person_Height_ad0db3fc-162d-4483-9f1f-371d99b6bd16.png?v=1770021352&amp;width=200" x="260" y="72" width="482" height="1778" preserveAspectRatio="xMidYMax meet"/>
    <text x="500" y="2000" text-anchor="middle" data-guide-person></text>
    <rect data-guide-frame fill="#262626"/>
    <image data-guide-art preserveAspectRatio="none"/>
    <g stroke="#777" stroke-width="6" fill="none"><path data-guide-width-line/><path data-guide-height-line/></g>
    <text data-guide-width text-anchor="middle"/><text data-guide-height text-anchor="middle"/>
  </svg><p class="laboo-scale-guide-note">Illustrative comparison. Measurements are for the print; frame shown for scale and not included.</p>`;
    const svg = container.querySelector("svg"), get = (name) => container.querySelector(`[data-guide-${name}]`);
    function attrs(el, values) {
      for (const [key, value] of Object.entries(values)) el.setAttribute(key, String(value));
    }
    return {
      update(model, finish) {
        if (!model) return;
        const [width, height] = model.mm, x = 1600 - width / 2, y = 800 - height / 2, edge = 12;
        attrs(get("frame"), { x: x - edge, y: y - edge, width: width + edge * 2, height: height + edge * 2 });
        get("frame").setAttribute("fill", finish === "natural-wood" ? "#c9ad88" : finish === "white" ? "#e7e5e0" : "#262626");
        get("frame").setAttribute("visibility", finish === "none" ? "hidden" : "visible");
        attrs(get("art"), { x, y, width, height });
        get("width-line").setAttribute("d", `M${x} ${y - 70}v-60m0 30h${width}m0-30v60`);
        get("height-line").setAttribute("d", `M${x + width + 70} ${y}h60m-30 0v${height}m-30 0h60`);
        attrs(get("width"), { x: 1600, y: y - 150 });
        get("width").textContent = model.widthText;
        attrs(get("height"), { x: x + width + 210, y: 800, transform: `rotate(90 ${x + width + 210} 800)` });
        get("height").textContent = model.heightText;
        get("person").textContent = model.personLabel;
        const label = `${model.label} print compared with a ${model.personLabel.replace("Person: ", "")} person.${finish === "none" ? " Print only." : " Frame illustrative."}`;
        svg.setAttribute("aria-label", label);
        get("title").textContent = label;
        Object.assign(svg.dataset, { widthMm: String(width), heightMm: String(height), personMm: String(PERSON_MM), units: model.unit });
      },
      setArtwork(url) {
        get("art").setAttribute("href", url);
      }
    };
  }

  // personalisation-product-integration/pdf-proof-renderer.mjs
  var libraryPromise;
  async function within(promise, message) {
    let timer;
    try {
      return await Promise.race([promise, new Promise((_, reject) => {
        timer = setTimeout(() => reject(Error(message)), 3e4);
      })]);
    } finally {
      clearTimeout(timer);
    }
  }
  async function renderSavedProof(saved, { rendererUrl, workerUrl, canvas, onProgress = () => {
  } }) {
    if (!rendererUrl || !workerUrl || !saved.printGeometry) throw Error("Please reload the page to prepare the latest proof.");
    onProgress("Loading your saved print file\u2026");
    const response = await fetch(saved.fileUrl, { cache: "no-store", credentials: "omit", signal: AbortSignal.timeout(2e4) });
    if (!response.ok) throw Error("Your saved print file could not be loaded. Please try again.");
    const bytes = new Uint8Array(await response.arrayBuffer());
    const hash = [...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))].map((n) => n.toString(16).padStart(2, "0")).join("");
    if (hash !== saved.sha256) throw Error("Your saved print file could not be verified. Please prepare it again.");
    onProgress("Opening your final proof\u2026");
    libraryPromise ||= import(rendererUrl).catch((e) => {
      libraryPromise = null;
      throw e;
    });
    const library = await within(libraryPromise, "Your proof viewer took too long to load. Please try again.");
    library.GlobalWorkerOptions.workerSrc = new URL(workerUrl, document.baseURI).href;
    const task = library.getDocument({ data: bytes, isEvalSupported: false, enableXfa: false });
    try {
      onProgress("Preparing your PDF preview\u2026");
      const document2 = await within(task.promise, "Your PDF preview took too long to open. Please try again.");
      if (document2.numPages !== 1) throw Error("Your print file has an unexpected page count.");
      const page = await document2.getPage(1), original = page.getViewport({ scale: 1 }), g = saved.printGeometry;
      if (!(await within(page.getOperatorList(), "Your print file took too long to prepare. Please try again.")).fnArray.length) throw Error("Your print proof has no drawing content. Please try again.");
      if (Math.abs(original.width * 25.4 / 72 - g.fileWidthMm) > 0.02 || Math.abs(original.height * 25.4 / 72 - g.fileHeightMm) > 0.02) throw Error("Your print dimensions could not be verified.");
      const viewport = page.getViewport({ scale: Math.min(2400 / original.width, 3200 / original.height) });
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      canvas.hidden = false;
      onProgress("Drawing your final proof\u2026");
      await within(page.render({ canvasContext: canvas.getContext("2d"), viewport, background: "white" }).promise, "Your print proof took too long to draw. Please try again.");
      const pixels = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height).data;
      let coloured = 0;
      for (let i = 0; i < pixels.length; i += 4 * 97) if (pixels[i] < 240 || pixels[i + 1] < 240 || pixels[i + 2] < 240) coloured++;
      if (coloured < 10) throw Error("Your print proof did not draw correctly. Please try again.");
      return true;
    } finally {
      void task.destroy().catch(() => {
      });
    }
  }

  // personalisation-product-integration/proof-magnifier.mjs
  function magnifierCrop(width, height, displayWidth, displayHeight, x, y, size, zoom = 2) {
    const w = Math.min(width, size / zoom * width / displayWidth), h = Math.min(height, size / zoom * height / displayHeight);
    return { x: Math.max(0, Math.min(width - w, x * width - w / 2)), y: Math.max(0, Math.min(height - h, y * height - h / 2)), width: w, height: h };
  }
  function createProofMagnifier(pane, { nativeMobileZoom = false } = {}) {
    const stage = pane.querySelector(".laboo-final-proof-image"), sheet = pane.querySelector(".laboo-proof-trim-sheet");
    const toolbar = document.createElement("div");
    toolbar.className = "laboo-proof-zoom-tools";
    toolbar.innerHTML = '<button type="button" data-proof-zoom aria-pressed="false" disabled>Zoom details</button><span>Hover to magnify \xB7 On touch screens, turn on zoom and drag.</span>';
    if (nativeMobileZoom) {
      const hint = document.createElement("span");
      hint.className = "laboo-proof-pinch-hint";
      hint.textContent = "Pinch to zoom";
      toolbar.append(hint);
    }
    stage.after(toolbar);
    const lens = document.createElement("div");
    lens.className = "laboo-proof-magnifier";
    lens.hidden = true;
    lens.setAttribute("aria-hidden", "true");
    lens.innerHTML = "<canvas></canvas><span>2\xD7 detail</span>";
    stage.append(lens);
    const marker = document.createElement("div");
    marker.className = "laboo-proof-zoom-marker";
    marker.hidden = true;
    marker.setAttribute("aria-hidden", "true");
    sheet.append(marker);
    const button = toolbar.querySelector("button"), canvas = lens.querySelector("canvas");
    button.title = "Turn on zoom, then use the arrow keys or drag across the proof to inspect details.";
    let source = null, active = false, point = { x: 0.5, y: 0.5 }, frame = 0, dragging = false;
    function hide() {
      cancelAnimationFrame(frame);
      frame = 0;
      lens.hidden = true;
      marker.hidden = true;
    }
    function draw() {
      frame = 0;
      if (!source || source.hidden || !source.getClientRects().length) {
        hide();
        return;
      }
      const r = source.getBoundingClientRect(), s = stage.getBoundingClientRect(), size = Math.min(150, s.width - 16, s.height - 16);
      if (size <= 0) return;
      const crop = magnifierCrop(source.width, source.height, r.width, r.height, point.x, point.y, size);
      const dpr = Math.min(window.devicePixelRatio || 1, 2), pixels = Math.round(size * dpr);
      if (canvas.width !== pixels) {
        canvas.width = pixels;
        canvas.height = pixels;
      }
      const context = canvas.getContext("2d");
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(source, crop.x, crop.y, crop.width, crop.height, 0, 0, pixels, pixels);
      lens.style.width = lens.style.height = size + "px";
      const px = r.left - s.left + point.x * r.width, py = r.top - s.top + point.y * r.height;
      lens.style.left = Math.max(8, Math.min(s.width - size - 8, px - size / 2)) + "px";
      lens.style.top = Math.max(8, Math.min(s.height - size - 8, py - size - 22 >= 8 ? py - size - 22 : py + 22)) + "px";
      marker.style.left = crop.x / source.width * 100 + "%";
      marker.style.top = crop.y / source.height * 100 + "%";
      marker.style.width = crop.width / source.width * 100 + "%";
      marker.style.height = crop.height / source.height * 100 + "%";
      lens.hidden = marker.hidden = false;
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(draw);
    }
    function move(event) {
      if (!source) return;
      const r = source.getBoundingClientRect();
      point = { x: Math.max(0, Math.min(1, (event.clientX - r.left) / r.width)), y: Math.max(0, Math.min(1, (event.clientY - r.top) / r.height)) };
      schedule();
    }
    function setActive(value) {
      active = value;
      button.setAttribute("aria-pressed", String(active));
      button.textContent = active ? "Close zoom" : "Zoom details";
      sheet.classList.toggle("laboo-proof-zoom-active", active);
      if (active) schedule();
      else hide();
    }
    button.addEventListener("click", () => setActive(!active));
    button.addEventListener("keydown", (event) => {
      const directions = { ArrowLeft: [-0.03, 0], ArrowRight: [0.03, 0], ArrowUp: [0, -0.03], ArrowDown: [0, 0.03] };
      if (event.key === "Escape") {
        setActive(false);
        return;
      }
      if (!directions[event.key]) return;
      event.preventDefault();
      if (!active) setActive(true);
      const [dx, dy] = directions[event.key];
      point = { x: Math.max(0, Math.min(1, point.x + dx)), y: Math.max(0, Math.min(1, point.y + dy)) };
      schedule();
    });
    const nativeZoom = () => nativeMobileZoom && window.matchMedia("(max-width:767px)").matches;
    sheet.addEventListener("pointermove", (event) => {
      if (nativeZoom()) return;
      if (event.pointerType === "mouse" || active && dragging) move(event);
    });
    sheet.addEventListener("pointerdown", (event) => {
      if (nativeZoom() || !active || !source) return;
      dragging = true;
      sheet.setPointerCapture(event.pointerId);
      move(event);
    });
    sheet.addEventListener("pointerup", () => {
      dragging = false;
    });
    sheet.addEventListener("pointercancel", () => {
      dragging = false;
      hide();
    });
    sheet.addEventListener("pointerleave", () => {
      if (!active) hide();
    });
    pane.closest("dialog")?.addEventListener("close", () => setActive(false));
    pane.closest(".laboo-customiser-body")?.addEventListener("scroll", () => {
      hide();
    }, { passive: true });
    window.addEventListener("resize", () => {
      if (nativeZoom()) setActive(false);
      else hide();
    });
    return { setSource(value) {
      hide();
      setActive(false);
      source = value;
      button.disabled = !source;
      if (source) point = { x: 0.5, y: 0.5 };
    } };
  }

  // personalisation-product-integration/final-proof-panel.mjs
  function createFinalProofPanel(pane, { prepareProof, rendererUrl, workerUrl, country = "GB", onChange = () => {
  }, isActive = () => true, personalisationType, mobileApprovalPrompt = false }) {
    pane.classList.add("laboo-final-proof");
    pane.innerHTML = '<figure class="laboo-final-proof-figure"><div class="laboo-final-proof-image" aria-busy="false"><div class="laboo-proof-state" data-proof-state-panel hidden><div role="status" aria-live="polite" aria-atomic="true"><span class="laboo-proof-spinner" data-proof-spinner aria-hidden="true"></span><strong data-proof-state-title></strong><p data-proof-state-note></p></div><button type="button" data-proof-state-retry hidden>Try again</button></div><div class="laboo-proof-trim-sheet"><img data-final-proof-image alt="Your final personalised print proof" hidden><div class="laboo-proof-bleed-mask" aria-hidden="true"></div><div class="laboo-proof-cut-edge" aria-hidden="true"><span class="laboo-proof-crop laboo-proof-crop-tl"></span><span class="laboo-proof-crop laboo-proof-crop-tr"></span><span class="laboo-proof-crop laboo-proof-crop-bl"></span><span class="laboo-proof-crop laboo-proof-crop-br"></span></div><div class="laboo-proof-width" data-proof-width></div><div class="laboo-proof-height"><span data-proof-height></span></div></div></div><figcaption><span class="laboo-proof-cut-key"></span> Dashed line: cut edge \xB7 <span class="laboo-proof-bleed-key"></span> Shaded outer strip: bleed removed when trimmed.<br>Rulers show the finished print size. Guides will not be printed.</figcaption></figure><div class="laboo-final-proof-details"><h2 tabindex="-1">Check your final proof</h2><section class="laboo-proof-product-summary" data-proof-product-summary aria-label="What you\u2019ll receive" hidden><div class="laboo-proof-product-thumb"><img data-proof-product-image width="112" height="140" alt="" decoding="async" hidden><span data-proof-product-image-status>Loading preview\u2026</span></div><div><h3>What you\u2019ll receive</h3><dl><div><dt>Print size</dt><dd data-proof-product-size></dd></div><div><dt>Finish</dt><dd data-proof-product-finish></dd></div></dl><p data-proof-product-inclusions></p></div></section><p class="laboo-step-intro">This preview comes directly from your saved print PDF. The area inside the dashed line is your finished print. Any white areas shown are part of your print.</p><p class="laboo-order-summary"></p><p class="laboo-proof-dimensions" data-proof-dimensions></p><p data-final-proof-status role="status" aria-live="polite"></p><button type="button" data-proof-retry hidden>Try preparing your proof again</button><p class="laboo-proof-pdf-link"><a data-final-proof-pdf target="_blank" rel="noopener" hidden>View the saved print PDF</a></p><label class="laboo-final-proof-consent"><input type="checkbox" data-proof-confirm disabled> <span>I have checked the name, number, size and layout and approve this print.</span></label><p class="laboo-proof-error" data-proof-error role="status" aria-live="polite"></p></div>';
    let image3 = pane.querySelector("[data-final-proof-image]");
    if (personalisationType === "personalised") pane.querySelector(".laboo-final-proof-consent span").textContent = "I have checked the words, dates, size and layout and approve this print.";
    if (personalisationType === "map") pane.querySelector(".laboo-final-proof-consent span").textContent = "I have checked the map location, words, size, layout and frame colour and approve this print.";
    const consent = pane.querySelector("[data-proof-confirm]"), status = pane.querySelector("[data-final-proof-status]"), error = pane.querySelector("[data-proof-error]"), retry = pane.querySelector("[data-proof-retry]"), pdf = pane.querySelector("[data-final-proof-pdf]");
    const stage = pane.querySelector(".laboo-final-proof-image"), statePanel = pane.querySelector("[data-proof-state-panel]"), stateTitle = pane.querySelector("[data-proof-state-title]"), stateNote = pane.querySelector("[data-proof-state-note]"), spinner = pane.querySelector("[data-proof-spinner]"), stateRetry = pane.querySelector("[data-proof-state-retry]");
    function setProofState(state, message = "") {
      const loading = state === "loading";
      stage.dataset.proofState = state;
      stage.setAttribute("aria-busy", String(loading));
      statePanel.hidden = state !== "loading" && state !== "error";
      spinner.hidden = !loading;
      stateRetry.hidden = state !== "error";
      stateTitle.textContent = loading ? "Preparing your print preview\u2026" : "We couldn\u2019t load your preview.";
      stateNote.textContent = message || (loading ? "This can take a moment. Please keep this window open." : "Please try again.");
    }
    const productSummary = pane.querySelector("[data-proof-product-summary]"), productImage = pane.querySelector("[data-proof-product-image]"), productImageStatus = pane.querySelector("[data-proof-product-image-status]");
    function clearProductSummary() {
      productSummary.hidden = true;
      productImage.onload = productImage.onerror = null;
      productImage.removeAttribute("src");
      productImage.hidden = true;
      productImageStatus.hidden = false;
      productImageStatus.textContent = "Loading preview\u2026";
    }
    function updateProductSummary(result, current) {
      const framed = !!result.finish && result.finish !== "none";
      const finish = framed ? { "natural-wood": "Natural wood", "dark-wood": "Dark wood", black: "Black", white: "White" }[result.finish] || result.frameLabel : "Print only";
      pane.querySelector("[data-proof-product-size]").textContent = result.size;
      pane.querySelector("[data-proof-product-finish]").textContent = framed ? finish + " frame" : "Print only";
      pane.querySelector("[data-proof-product-inclusions]").textContent = framed ? "Your artwork is fitted inside the frame with protective Perspex. Arrives assembled and ready to hang." : "Unframed print on matte paper. Frame not included.";
      productSummary.hidden = false;
      productSummary.dataset.finish = result.finish || "none";
      productImage.alt = framed ? "Your personalised artwork in the selected " + finish.toLowerCase() + " frame. Frame included." : "Your personalised unframed print. Frame not included.";
      const url = framed ? result.cartPreviewUrl : result.previewUrl;
      productImage.onload = () => {
        if (current !== version) return;
        productImage.hidden = false;
        productImageStatus.hidden = true;
      };
      productImage.onerror = () => {
        if (current !== version) return;
        productImage.hidden = true;
        productImageStatus.hidden = false;
        productImageStatus.textContent = "Preview unavailable";
      };
      if (url) productImage.src = url;
      else productImageStatus.textContent = "Preview unavailable";
    }
    const magnifier = createProofMagnifier(pane, { nativeMobileZoom: mobileApprovalPrompt });
    const approvalLabel = consent.closest("label");
    error.id = "laboo-final-proof-approval-error";
    consent.setAttribute("aria-describedby", error.id);
    const clearApprovalError = () => {
      approvalLabel.classList.remove("laboo-proof-consent-error");
      consent.removeAttribute("aria-invalid");
      error.textContent = "";
    };
    let saved = null, version = 0, imageReady = false;
    function invalidate() {
      version++;
      clearProductSummary();
      setProofState("idle");
      saved = null;
      imageReady = false;
      pane.querySelector("[data-gift-proof-review]")?.remove();
      magnifier.setSource(null);
      consent.checked = false;
      consent.disabled = true;
      image3.hidden = true;
      pdf.hidden = true;
      retry.hidden = true;
      clearApprovalError();
      pane.querySelector(".laboo-proof-trim-sheet").dataset.ready = "false";
      for (const selector of ["[data-proof-width]", "[data-proof-height]", "[data-proof-dimensions]"]) pane.querySelector(selector).textContent = "";
      if (isActive()) status.textContent = "Your design changed. Go back and prepare a new final proof.";
      onChange();
    }
    async function prepare() {
      invalidate();
      const current = version;
      setProofState("loading");
      status.textContent = "Preparing your final print proof\u2026";
      try {
        const result = await prepareProof();
        if (current !== version || !isActive()) {
          if (current === version) setProofState("idle");
          return;
        }
        const description = result.personalisationSummary || `${result.name} / ${result.number}`;
        updateProductSummary(result, current);
        const canvas = document.createElement("canvas");
        canvas.hidden = true;
        canvas.setAttribute("data-final-proof-image", "");
        canvas.setAttribute("role", "img");
        canvas.setAttribute("aria-label", `Final proof: ${description}, ${result.size}`);
        image3.replaceWith(canvas);
        image3 = canvas;
        await renderSavedProof(result, { rendererUrl, workerUrl, canvas, onProgress: (message) => {
          if (current === version && isActive()) status.textContent = message;
        } });
        if (current !== version || !isActive()) {
          if (current === version) setProofState("idle");
          return;
        }
        saved = result;
        const g = result.printGeometry, b = g.bleedMm, sheet = pane.querySelector(".laboo-proof-trim-sheet");
        for (const [side, value] of Object.entries(b)) sheet.style.setProperty("--proof-" + side, `${100 * value / (side === "top" || side === "bottom" ? g.fileHeightMm : g.fileWidthMm)}%`);
        const format = (mm) => `${Number((mm / (country === "US" ? 25.4 : 10)).toFixed(2))} ${country === "US" ? "in" : "cm"}`;
        pane.querySelector("[data-proof-width]").textContent = format(g.widthMm);
        pane.querySelector("[data-proof-height]").textContent = format(g.heightMm);
        pane.querySelector("[data-proof-dimensions]").textContent = `Finished print: ${format(g.widthMm)} \xD7 ${format(g.heightMm)}. Bleed outside the cut edge: ${b.left} mm left, ${b.right} mm right, ${b.top} mm top and ${b.bottom} mm bottom.`;
        pane.querySelector("[data-gift-proof-review]")?.remove();
        if (result.gift) {
          const review = document.createElement("section");
          review.dataset.giftProofReview = "";
          review.className = "laboo-gift-proof-review";
          const heading = document.createElement("strong");
          heading.textContent = "Your gift message";
          const recipient = document.createElement("p");
          recipient.textContent = "To " + result.gift.recipientName + " \xB7 " + result.gift.recipientEmail;
          const note = document.createElement("p");
          note.style.whiteSpace = "pre-wrap";
          note.textContent = result.gift.message;
          const sender = document.createElement("p");
          sender.textContent = "From " + result.gift.fromName;
          const timing = document.createElement("p");
          timing.textContent = "Emailed after courier-confirmed delivery. Your gift note is separate from the printed artwork.";
          review.append(heading, recipient, note, sender, timing);
          pane.querySelector(".laboo-final-proof-consent").before(review);
        }
        const frame = result.finish && result.finish !== "none";
        pane.querySelector(".laboo-order-summary").textContent = description;
        pane.querySelector(".laboo-final-proof-consent span").textContent = (result.personalisationType || personalisationType) === "personalised" ? frame ? "I have checked the words, dates, size, layout and selected frame and approve this ready-to-hang print." : "I have checked the words, dates, size and layout and approve this print." : result.personalisationType === "map" ? frame ? "I have checked the map location, words, size, layout and frame colour and approve this ready-to-hang print." : "I have checked the map location, words, size and layout and approve this print." : frame ? "I have checked the name, number, size, layout and frame colour and approve this ready-to-hang print." : "I have checked the name, number, size and layout and approve this print.";
        sheet.dataset.ready = "true";
        imageReady = true;
        setProofState("ready");
        magnifier.setSource(canvas);
        consent.disabled = false;
        status.textContent = frame ? "Check the print PDF and selected frame details, then tick the approval box. The frame covers approximately 4\u20135 mm at each edge." : "Check this saved proof, then tick the approval box to continue.";
        pdf.href = result.fileUrl;
        pdf.hidden = false;
        onChange();
      } catch (e) {
        if (current !== version) return;
        image3.hidden = true;
        status.textContent = e.message || "Your proof could not be prepared.";
        setProofState("error", status.textContent);
        retry.hidden = false;
        onChange();
      }
    }
    consent.addEventListener("change", () => {
      clearApprovalError();
      onChange();
    });
    retry.addEventListener("click", prepare);
    stateRetry.addEventListener("click", prepare);
    const approved = () => !!saved && imageReady && consent.checked && !consent.disabled;
    return { prepare, invalidate, isReady: () => !!saved && imageReady && !consent.disabled, isApproved: approved, getProof: () => approved() ? saved : null, validate() {
      if (approved()) return true;
      error.textContent = "Please check your final proof and tick the approval box.";
      if (mobileApprovalPrompt && window.matchMedia("(max-width:767px)").matches) {
        approvalLabel.classList.add("laboo-proof-consent-error");
        consent.setAttribute("aria-invalid", "true");
        consent.focus({ preventScroll: true });
        approvalLabel.scrollIntoView({ block: "center", behavior: window.matchMedia("(prefers-reduced-motion:reduce)").matches ? "instant" : "smooth" });
      } else consent.focus();
      return false;
    } };
  }

  // personalisation-product-integration/frame-regional-catalogue.mjs
  var FRAME_REGIONAL_PRODUCTS = { "metric": [["210x297", "natural-wood", [210, 297], "framed_poster_mounted_210x297mm-8x12-inch_natural-wood_wood_w12xt22-mm_plexiglass_a4-8x12-inch_200-gsm-80lb-uncoated_4-0_ver"], ["210x297", "dark-wood", [210, 297], "framed_poster_mounted_210x297mm-8x12-inch_dark-wood_wood_w12xt22-mm_plexiglass_a4-8x12-inch_200-gsm-80lb-uncoated_4-0_ver"], ["210x297", "black", [210, 297], "framed_poster_mounted_210x297mm-8x12-inch_black_wood_w12xt22-mm_plexiglass_a4-8x12-inch_200-gsm-80lb-uncoated_4-0_ver"], ["210x297", "white", [210, 297], "framed_poster_mounted_210x297mm-8x12-inch_white_wood_w12xt22-mm_plexiglass_a4-8x12-inch_200-gsm-80lb-uncoated_4-0_ver"], ["297x420", "natural-wood", [297, 420], "framed_poster_mounted_a3_natural-wood_wood_w12xt22-mm_plexiglass_a3_200-gsm-80lb-uncoated_4-0_ver"], ["297x420", "dark-wood", [297, 420], "framed_poster_mounted_a3_dark-wood_wood_w12xt22-mm_plexiglass_a3_200-gsm-80lb-uncoated_4-0_ver"], ["297x420", "black", [297, 420], "framed_poster_mounted_a3_black_wood_w12xt22-mm_plexiglass_a3_200-gsm-80lb-uncoated_4-0_ver"], ["297x420", "white", [297, 420], "framed_poster_mounted_a3_white_wood_w12xt22-mm_plexiglass_a3_200-gsm-80lb-uncoated_4-0_ver"], ["420x594", "natural-wood", [420, 594], "framed_poster_mounted_a2_natural-wood_wood_w12xt22-mm_plexiglass_a2_200-gsm-80lb-uncoated_4-0_ver"], ["420x594", "dark-wood", [420, 594], "framed_poster_mounted_a2_dark-wood_wood_w12xt22-mm_plexiglass_a2_200-gsm-80lb-uncoated_4-0_ver"], ["420x594", "black", [420, 594], "framed_poster_mounted_a2_black_wood_w12xt22-mm_plexiglass_a2_200-gsm-80lb-uncoated_4-0_ver"], ["420x594", "white", [420, 594], "framed_poster_mounted_a2_white_wood_w12xt22-mm_plexiglass_a2_200-gsm-80lb-uncoated_4-0_ver"], ["594x841", "natural-wood", [594, 841], "framed_poster_mounted_a1_natural-wood_wood_w12xt22-mm_plexiglass_a1_200-gsm-80lb-uncoated_4-0_ver"], ["594x841", "dark-wood", [594, 841], "framed_poster_mounted_a1_dark-wood_wood_w12xt22-mm_plexiglass_a1_200-gsm-80lb-uncoated_4-0_ver"], ["594x841", "black", [594, 841], "framed_poster_mounted_a1_black_wood_w12xt22-mm_plexiglass_a1_200-gsm-80lb-uncoated_4-0_ver"], ["594x841", "white", [594, 841], "framed_poster_mounted_a1_white_wood_w12xt22-mm_plexiglass_a1_200-gsm-80lb-uncoated_4-0_ver"], ["270x350", "natural-wood", [270, 350], "framed_poster_mounted_270x350-mm-11x14-inch_natural-wood_wood_w12xt22-mm_plexiglass_270x350-mm-11x14-inch_200-gsm-80lb-uncoated_4-0_ver"], ["270x350", "dark-wood", [270, 350], "framed_poster_mounted_270x350-mm-11x14-inch_dark-wood_wood_w12xt22-mm_plexiglass_270x350-mm-11x14-inch_200-gsm-80lb-uncoated_4-0_ver"], ["270x350", "black", [270, 350], "framed_poster_mounted_270x350-mm-11x14-inch_black_wood_w12xt22-mm_plexiglass_270x350-mm-11x14-inch_200-gsm-80lb-uncoated_4-0_ver"], ["270x350", "white", [270, 350], "framed_poster_mounted_270x350-mm-11x14-inch_white_wood_w12xt22-mm_plexiglass_270x350-mm-11x14-inch_200-gsm-80lb-uncoated_4-0_ver"], ["300x400", "natural-wood", [300, 400], "framed_poster_mounted_300x400-mm-12x16-inch_natural-wood_wood_w12xt22-mm_plexiglass_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver"], ["300x400", "dark-wood", [300, 400], "framed_poster_mounted_300x400-mm-12x16-inch_dark-wood_wood_w12xt22-mm_plexiglass_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver"], ["300x400", "black", [300, 400], "framed_poster_mounted_300x400-mm-12x16-inch_black_wood_w12xt22-mm_plexiglass_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver"], ["300x400", "white", [300, 400], "framed_poster_mounted_300x400-mm-12x16-inch_white_wood_w12xt22-mm_plexiglass_300x400-mm-12x16-inch_200-gsm-80lb-uncoated_4-0_ver"], ["400x500", "natural-wood", [400, 500], "framed_poster_mounted_400x500-mm-16x20-inch_natural-wood_wood_w12xt22-mm_plexiglass_400x500-mm-16x20-inch_200-gsm-80lb-uncoated_4-0_ver"], ["400x500", "dark-wood", [400, 500], "framed_poster_mounted_400x500-mm-16x20-inch_dark-wood_wood_w12xt22-mm_plexiglass_400x500-mm-16x20-inch_200-gsm-80lb-uncoated_4-0_ver"], ["400x500", "black", [400, 500], "framed_poster_mounted_400x500-mm-16x20-inch_black_wood_w12xt22-mm_plexiglass_400x500-mm-16x20-inch_200-gsm-80lb-uncoated_4-0_ver"], ["400x500", "white", [400, 500], "framed_poster_mounted_400x500-mm-16x20-inch_white_wood_w12xt22-mm_plexiglass_400x500-mm-16x20-inch_200-gsm-80lb-uncoated_4-0_ver"], ["400x600", "natural-wood", [400, 600], "framed_poster_mounted_400x600-mm-16x24-inch_natural-wood_wood_w12xt22-mm_plexiglass_400x600-mm-16x24-inch_200-gsm-80lb-uncoated_4-0_ver"], ["400x600", "dark-wood", [400, 600], "framed_poster_mounted_400x600-mm-16x24-inch_dark-wood_wood_w12xt22-mm_plexiglass_400x600-mm-16x24-inch_200-gsm-80lb-uncoated_4-0_ver"], ["400x600", "black", [400, 600], "framed_poster_mounted_400x600-mm-16x24-inch_black_wood_w12xt22-mm_plexiglass_400x600-mm-16x24-inch_200-gsm-80lb-uncoated_4-0_ver"], ["400x600", "white", [400, 600], "framed_poster_mounted_400x600-mm-16x24-inch_white_wood_w12xt22-mm_plexiglass_400x600-mm-16x24-inch_200-gsm-80lb-uncoated_4-0_ver"], ["500x700", "natural-wood", [500, 700], "framed_poster_mounted_500x700-mm-20x28-inch_natural-wood_wood_w12xt22-mm_plexiglass_500x700-mm-20x28-inch_200-gsm-80lb-uncoated_4-0_ver"], ["500x700", "dark-wood", [500, 700], "framed_poster_mounted_500x700-mm-20x28-inch_dark-wood_wood_w12xt22-mm_plexiglass_500x700-mm-20x28-inch_200-gsm-80lb-uncoated_4-0_ver"], ["500x700", "black", [500, 700], "framed_poster_mounted_500x700-mm-20x28-inch_black_wood_w12xt22-mm_plexiglass_500x700-mm-20x28-inch_200-gsm-80lb-uncoated_4-0_ver"], ["500x700", "white", [500, 700], "framed_poster_mounted_500x700-mm-20x28-inch_white_wood_w12xt22-mm_plexiglass_500x700-mm-20x28-inch_200-gsm-80lb-uncoated_4-0_ver"], ["600x800", "natural-wood", [600, 800], "framed_poster_mounted_600x800-mm-24x32-inch_natural-wood_wood_w12xt22-mm_plexiglass_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver"], ["600x800", "dark-wood", [600, 800], "framed_poster_mounted_600x800-mm-24x32-inch_dark-wood_wood_w12xt22-mm_plexiglass_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver"], ["600x800", "black", [600, 800], "framed_poster_mounted_600x800-mm-24x32-inch_black_wood_w12xt22-mm_plexiglass_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver"], ["600x800", "white", [600, 800], "framed_poster_mounted_600x800-mm-24x32-inch_white_wood_w12xt22-mm_plexiglass_600x800-mm-24x32-inch_200-gsm-80lb-uncoated_4-0_ver"], ["600x900", "natural-wood", [600, 900], "framed_poster_mounted_600x900-mm-24x36-inch_natural-wood_wood_w12xt22-mm_plexiglass_600x900-mm-24x36-inch_200-gsm-80lb-uncoated_4-0_ver"], ["600x900", "dark-wood", [600, 900], "framed_poster_mounted_600x900-mm-24x36-inch_dark-wood_wood_w12xt22-mm_plexiglass_600x900-mm-24x36-inch_200-gsm-80lb-uncoated_4-0_ver"], ["600x900", "black", [600, 900], "framed_poster_mounted_600x900-mm-24x36-inch_black_wood_w12xt22-mm_plexiglass_600x900-mm-24x36-inch_200-gsm-80lb-uncoated_4-0_ver"], ["600x900", "white", [600, 900], "framed_poster_mounted_600x900-mm-24x36-inch_white_wood_w12xt22-mm_plexiglass_600x900-mm-24x36-inch_200-gsm-80lb-uncoated_4-0_ver"], ["700x1000", "natural-wood", [700, 1e3], "framed_poster_mounted_700x1000-mm-28x40-inch_natural-wood_wood_w12xt22-mm_plexiglass_700x1000-mm-28x40-inch_200-gsm-80lb-uncoated_4-0_ver"], ["700x1000", "dark-wood", [700, 1e3], "framed_poster_mounted_700x1000-mm-28x40-inch_dark-wood_wood_w12xt22-mm_plexiglass_700x1000-mm-28x40-inch_200-gsm-80lb-uncoated_4-0_ver"], ["700x1000", "black", [700, 1e3], "framed_poster_mounted_700x1000-mm-28x40-inch_black_wood_w12xt22-mm_plexiglass_700x1000-mm-28x40-inch_200-gsm-80lb-uncoated_4-0_ver"], ["700x1000", "white", [700, 1e3], "framed_poster_mounted_700x1000-mm-28x40-inch_white_wood_w12xt22-mm_plexiglass_700x1000-mm-28x40-inch_200-gsm-80lb-uncoated_4-0_ver"]], "northAmerica": [["210x297", "natural-wood", [203.2, 304.8], "framed_poster_mounted_8x12-inch-210x297-mm_natural-wood_wood_w12xt22-mm_plexiglass_8x12-inch-a4_200-gsm-80lb-uncoated_4-0_ver"], ["210x297", "dark-wood", [203.2, 304.8], "framed_poster_mounted_8x12-inch-210x297-mm_dark-wood_wood_w12xt22-mm_plexiglass_8x12-inch-a4_200-gsm-80lb-uncoated_4-0_ver"], ["210x297", "black", [203.2, 304.8], "framed_poster_mounted_8x12-inch-210x297-mm_black_wood_w12xt22-mm_plexiglass_8x12-inch-a4_200-gsm-80lb-uncoated_4-0_ver"], ["210x297", "white", [203.2, 304.8], "framed_poster_mounted_8x12-inch-210x297-mm_white_wood_w12xt22-mm_plexiglass_8x12-inch-a4_200-gsm-80lb-uncoated_4-0_ver"], ["297x420", "natural-wood", [304.8, 406.4], "framed_poster_mounted_12x16-inch-300x400-mm_natural-wood_wood_w12xt22-mm_plexiglass_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver"], ["297x420", "dark-wood", [304.8, 406.4], "framed_poster_mounted_12x16-inch-300x400-mm_dark-wood_wood_w12xt22-mm_plexiglass_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver"], ["297x420", "black", [304.8, 406.4], "framed_poster_mounted_12x16-inch-300x400-mm_black_wood_w12xt22-mm_plexiglass_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver"], ["297x420", "white", [304.8, 406.4], "framed_poster_mounted_12x16-inch-300x400-mm_white_wood_w12xt22-mm_plexiglass_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver"], ["420x594", "natural-wood", [406.4, 609.6], "framed_poster_mounted_16x24-inch-400x600-mm_natural-wood_wood_w12xt22-mm_plexiglass_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver"], ["420x594", "dark-wood", [406.4, 609.6], "framed_poster_mounted_16x24-inch-400x600-mm_dark-wood_wood_w12xt22-mm_plexiglass_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver"], ["420x594", "black", [406.4, 609.6], "framed_poster_mounted_16x24-inch-400x600-mm_black_wood_w12xt22-mm_plexiglass_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver"], ["420x594", "white", [406.4, 609.6], "framed_poster_mounted_16x24-inch-400x600-mm_white_wood_w12xt22-mm_plexiglass_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver"], ["594x841", "natural-wood", [609.6, 812.8], "framed_poster_mounted_24x32-inch-600x800-mm_natural-wood_wood_w12xt22-mm_plexiglass_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver"], ["594x841", "dark-wood", [609.6, 812.8], "framed_poster_mounted_24x32-inch-600x800-mm_dark-wood_wood_w12xt22-mm_plexiglass_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver"], ["594x841", "black", [609.6, 812.8], "framed_poster_mounted_24x32-inch-600x800-mm_black_wood_w12xt22-mm_plexiglass_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver"], ["594x841", "white", [609.6, 812.8], "framed_poster_mounted_24x32-inch-600x800-mm_white_wood_w12xt22-mm_plexiglass_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver"], ["270x350", "natural-wood", [279.4, 355.6], "framed_poster_mounted_11x14-inch-270x350-mm_natural-wood_wood_w12xt22-mm_plexiglass_11x14-inch-270x350-mm_200-gsm-80lb-uncoated_4-0_ver"], ["270x350", "dark-wood", [279.4, 355.6], "framed_poster_mounted_11x14-inch-270x350-mm_dark-wood_wood_w12xt22-mm_plexiglass_11x14-inch-270x350-mm_200-gsm-80lb-uncoated_4-0_ver"], ["270x350", "black", [279.4, 355.6], "framed_poster_mounted_11x14-inch-270x350-mm_black_wood_w12xt22-mm_plexiglass_11x14-inch-270x350-mm_200-gsm-80lb-uncoated_4-0_ver"], ["270x350", "white", [279.4, 355.6], "framed_poster_mounted_11x14-inch-270x350-mm_white_wood_w12xt22-mm_plexiglass_11x14-inch-270x350-mm_200-gsm-80lb-uncoated_4-0_ver"], ["300x400", "natural-wood", [304.8, 406.4], "framed_poster_mounted_12x16-inch-300x400-mm_natural-wood_wood_w12xt22-mm_plexiglass_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver"], ["300x400", "dark-wood", [304.8, 406.4], "framed_poster_mounted_12x16-inch-300x400-mm_dark-wood_wood_w12xt22-mm_plexiglass_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver"], ["300x400", "black", [304.8, 406.4], "framed_poster_mounted_12x16-inch-300x400-mm_black_wood_w12xt22-mm_plexiglass_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver"], ["300x400", "white", [304.8, 406.4], "framed_poster_mounted_12x16-inch-300x400-mm_white_wood_w12xt22-mm_plexiglass_12x16-inch-300x400-mm_200-gsm-80lb-uncoated_4-0_ver"], ["400x500", "natural-wood", [406.4, 508], "framed_poster_mounted_16x20-inch-400x500-mm_natural-wood_wood_w12xt22-mm_plexiglass_16x20-inch-400x500-mm_200-gsm-80lb-uncoated_4-0_ver"], ["400x500", "dark-wood", [406.4, 508], "framed_poster_mounted_16x20-inch-400x500-mm_dark-wood_wood_w12xt22-mm_plexiglass_16x20-inch-400x500-mm_200-gsm-80lb-uncoated_4-0_ver"], ["400x500", "black", [406.4, 508], "framed_poster_mounted_16x20-inch-400x500-mm_black_wood_w12xt22-mm_plexiglass_16x20-inch-400x500-mm_200-gsm-80lb-uncoated_4-0_ver"], ["400x500", "white", [406.4, 508], "framed_poster_mounted_16x20-inch-400x500-mm_white_wood_w12xt22-mm_plexiglass_16x20-inch-400x500-mm_200-gsm-80lb-uncoated_4-0_ver"], ["400x600", "natural-wood", [406.4, 609.6], "framed_poster_mounted_16x24-inch-400x600-mm_natural-wood_wood_w12xt22-mm_plexiglass_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver"], ["400x600", "dark-wood", [406.4, 609.6], "framed_poster_mounted_16x24-inch-400x600-mm_dark-wood_wood_w12xt22-mm_plexiglass_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver"], ["400x600", "black", [406.4, 609.6], "framed_poster_mounted_16x24-inch-400x600-mm_black_wood_w12xt22-mm_plexiglass_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver"], ["400x600", "white", [406.4, 609.6], "framed_poster_mounted_16x24-inch-400x600-mm_white_wood_w12xt22-mm_plexiglass_16x24-inch-400x600-mm_200-gsm-80lb-uncoated_4-0_ver"], ["500x700", "natural-wood", [508, 711.2], "framed_poster_mounted_20x28-inch-500x700-mm_natural-wood_wood_w12xt22-mm_plexiglass_20x28-inch-500x700-mm_200-gsm-80lb-uncoated_4-0_ver"], ["500x700", "dark-wood", [508, 711.2], "framed_poster_mounted_20x28-inch-500x700-mm_dark-wood_wood_w12xt22-mm_plexiglass_20x28-inch-500x700-mm_200-gsm-80lb-uncoated_4-0_ver"], ["500x700", "black", [508, 711.2], "framed_poster_mounted_20x28-inch-500x700-mm_black_wood_w12xt22-mm_plexiglass_20x28-inch-500x700-mm_200-gsm-80lb-uncoated_4-0_ver"], ["500x700", "white", [508, 711.2], "framed_poster_mounted_20x28-inch-500x700-mm_white_wood_w12xt22-mm_plexiglass_20x28-inch-500x700-mm_200-gsm-80lb-uncoated_4-0_ver"], ["600x800", "natural-wood", [609.6, 812.8], "framed_poster_mounted_24x32-inch-600x800-mm_natural-wood_wood_w12xt22-mm_plexiglass_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver"], ["600x800", "dark-wood", [609.6, 812.8], "framed_poster_mounted_24x32-inch-600x800-mm_dark-wood_wood_w12xt22-mm_plexiglass_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver"], ["600x800", "black", [609.6, 812.8], "framed_poster_mounted_24x32-inch-600x800-mm_black_wood_w12xt22-mm_plexiglass_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver"], ["600x800", "white", [609.6, 812.8], "framed_poster_mounted_24x32-inch-600x800-mm_white_wood_w12xt22-mm_plexiglass_24x32-inch-600x800-mm_200-gsm-80lb-uncoated_4-0_ver"], ["600x900", "natural-wood", [609.6, 914.4], "framed_poster_mounted_24x36-inch-600x900-mm_natural-wood_wood_w12xt22-mm_plexiglass_24x36-inch-600x900-mm_200-gsm-80lb-uncoated_4-0_ver"], ["600x900", "dark-wood", [609.6, 914.4], "framed_poster_mounted_24x36-inch-600x900-mm_dark-wood_wood_w12xt22-mm_plexiglass_24x36-inch-600x900-mm_200-gsm-80lb-uncoated_4-0_ver"], ["600x900", "black", [609.6, 914.4], "framed_poster_mounted_24x36-inch-600x900-mm_black_wood_w12xt22-mm_plexiglass_24x36-inch-600x900-mm_200-gsm-80lb-uncoated_4-0_ver"], ["600x900", "white", [609.6, 914.4], "framed_poster_mounted_24x36-inch-600x900-mm_white_wood_w12xt22-mm_plexiglass_24x36-inch-600x900-mm_200-gsm-80lb-uncoated_4-0_ver"], ["700x1000", "natural-wood", [711.2, 1016], "framed_poster_mounted_28x40-inch-700x1000-mm_natural-wood_wood_w12xt22-mm_plexiglass_28x40-inch-700x1000-mm_200-gsm-80lb-uncoated_4-0_ver"], ["700x1000", "dark-wood", [711.2, 1016], "framed_poster_mounted_28x40-inch-700x1000-mm_dark-wood_wood_w12xt22-mm_plexiglass_28x40-inch-700x1000-mm_200-gsm-80lb-uncoated_4-0_ver"], ["700x1000", "black", [711.2, 1016], "framed_poster_mounted_28x40-inch-700x1000-mm_black_wood_w12xt22-mm_plexiglass_28x40-inch-700x1000-mm_200-gsm-80lb-uncoated_4-0_ver"], ["700x1000", "white", [711.2, 1016], "framed_poster_mounted_28x40-inch-700x1000-mm_white_wood_w12xt22-mm_plexiglass_28x40-inch-700x1000-mm_200-gsm-80lb-uncoated_4-0_ver"]] };
  var FRAME_COUNTRY_AVAILABILITY = { "GB": "111111111111111111111111111111111111111111111111", "US": "111111111111111111111111111111111111111111111111", "CA": "111111111111111111111111111111111111111111111111", "FR": "111111111111111111111111111111111111111111111111", "AU": "111111111111111111111111111111111111111111111111", "AT": "111111111111111111111111111111111111111111111111", "BE": "111111111111111111111111111111111111111111111111", "BG": "111111111111111111111111111111111111111111111111", "HR": "111111111111111111111111111111111111111111111111", "CZ": "111111111111111111111111111111111111111111111111", "DK": "111111111111111111111111111111111111111111111111", "EE": "111111111111111111111111111111111111111111111111", "FI": "111111111111111111111111111111111111111111111111", "DE": "111111111111111111111111111111111111111111111111", "GR": "111111111111111111111111111111111111111111111111", "HU": "111111111111111111111111111111111111111111111111", "ID": "111111111111111111111111111111111111111111111111", "IE": "111111111111111111111111111111111111111111111111", "IL": "111111111111111111111111111111111111111111111111", "IT": "111111111111111111111111111111111111111111111111", "JP": "111111111111111111111111111111111111111111111111", "LV": "111111111111111111111111111111111111111111111111", "LT": "111111111111111111111111111111111111111111111111", "LU": "111111111111111111111111111111111111111111111111", "MY": "111111111111111111111111111111111111111111111111", "MT": "111111111111111111111111111111111111111111111111", "NL": "111111111111111111111111111111111111111111111111", "NZ": "111111111111111111111111111111111111111111111111", "NO": "111111111111111111111111111111111111111111111111", "PL": "111111111111111111111111111111111111111111111111", "PT": "111111111111111111111111111111111111111111111111", "SA": "111111111111111111111111111111111111111111111111", "SG": "111111111111111111111111111111111111111111111111", "SK": "111111111111111111111111111111111111111111111111", "SI": "111111111111111111111111111111111111111111111111", "KR": "111111111111111111111111111111111111111111111111", "ES": "111111111111111111111111111111111111111111111111", "SE": "111111111111111111111111111111111111111111111111", "CH": "111111111111111111111111111111111111111111111111", "TW": "111111111111111111111111111111111111111111111111", "TH": "111111111111111111111111111111111111111111111111", "TR": "111111111111111111111111111111111111111111111111", "AE": "111111111111111111111111111111111111111111111111" };

  // personalisation-product-integration/frame-regional.mjs
  var match = (a, b) => a.length === b.length && a.every((v, i) => Math.abs(v - b[i]) < 0.01);
  function regionalFrameProduct(mm, finish, country = "GB") {
    if (!Array.isArray(mm) || mm.length !== 2) return null;
    const region = ["US", "CA"].includes(country) ? "northAmerica" : "metric", entries = FRAME_REGIONAL_PRODUCTS[region], key = mm.map((n) => n === 595 ? 594 : n).join("x");
    const i = entries.findIndex(([size, colour, dimensions2]) => colour === finish && (size === key || match(dimensions2, mm)));
    if (i < 0 || FRAME_COUNTRY_AVAILABILITY[country]?.[i] !== "1") return null;
    const [sourceSize, , dimensions, productUid] = entries[i];
    return { sourceSize, mm: dimensions, productUid, country, assembly: "ready-to-hang" };
  }

  // personalisation-product-integration/standard-frame-geometry.mjs
  var FRAME_COLOURS = [["natural-wood", "Natural wood"], ["dark-wood", "Dark wood"], ["black", "Black"], ["white", "White"]];
  function frameEligibility(model) {
    if (!Array.isArray(model?.mm) || model.mm.length !== 2 || model.mm.some((n) => !Number.isFinite(n) || n <= 0)) return { eligible: false, reason: "Choose an available print size first." };
    const [w, h] = model.mm;
    if (w <= 130 && h <= 180) return { eligible: false, reason: "Frame not available in this size. Available as a print only." };
    if (w >= 750 || h > 1100) return { eligible: false, reason: "Frame not available in this size. Available as a print only." };
    if (model.country && !FRAME_COLOURS.some(([finish]) => regionalFrameProduct(model.mm, finish, model.country))) return { eligible: false, reason: "Frame not available for this size and delivery country. Your print is supplied unframed." };
    return { eligible: true, reason: "" };
  }

  // personalisation-product-integration/clean-frame-mockup.mjs
  var WOOD_PSD = { size: 1500, box: [331, 156, 1175, 1339] };
  function woodMockupLayout(mm, size = 1500) {
    const [left, top, right, bottom] = WOOD_PSD.box;
    const h = bottom - top, w = h * mm[0] / mm[1], x = (left + right - w) / 2;
    const scale = size / WOOD_PSD.size;
    return {
      x: x * scale,
      y: top * scale,
      w: w * scale,
      h: h * scale,
      sourceX: [0, left, right, 1500],
      sourceY: [0, top, bottom, 1500],
      targetX: [0, x, x + w, 1500].map((n) => n * scale),
      targetY: [0, top, bottom, 1500].map((n) => n * scale)
    };
  }
  function drawWoodOverlay(ctx, image3, g) {
    for (let row = 0; row < 3; row++) for (let col = 0; col < 3; col++) {
      const sx = g.sourceX[col], sy = g.sourceY[row], sw = g.sourceX[col + 1] - sx, sh = g.sourceY[row + 1] - sy;
      const dx = g.targetX[col], dy = g.targetY[row], dw = g.targetX[col + 1] - dx, dh = g.targetY[row + 1] - dy;
      ctx.drawImage(image3, sx, sy, sw, sh, dx, dy, dw, dh);
    }
  }
  function createCleanFrameMockup(canvas, mockupUrl, { onRender = () => {
  }, frameLabel = "frame not included" } = {}) {
    let version = 0;
    const overlays = /* @__PURE__ */ new Map();
    function overlayFor(finish) {
      if (finish === "none") return Promise.resolve(null);
      const key = finish === "natural-wood" ? "wood" : finish;
      if (!overlays.has(key)) overlays.set(key, new Promise((resolve) => {
        const overlay = new Image();
        overlay.crossOrigin = "anonymous";
        overlay.onload = () => resolve(overlay);
        overlay.onerror = () => resolve(null);
        const src = new URL(mockupUrl, "https://berahgewesjy.shop/");
        if (key !== "wood") {
          src.pathname = src.pathname.replace("laboo-supplied-wood-overlay.webp", `laboo-supplied-${key}-overlay.webp`);
          src.search = "";
        }
        overlay.src = src.href;
      }));
      return overlays.get(key);
    }
    return async function update(url, mm = [300, 400], finish = "natural-wood") {
      const current = ++version;
      canvas.setAttribute("aria-busy", "true");
      const artwork2 = new Image();
      await new Promise((resolve, reject) => {
        artwork2.onload = resolve;
        artwork2.onerror = () => reject(Error("Frame artwork unavailable"));
        artwork2.src = url;
      });
      const frame = await overlayFor(finish);
      if (current !== version) return;
      const framed = frameEligibility({ mm }).eligible && frame;
      const ctx = canvas.getContext("2d"), size = canvas.width;
      ctx.clearRect(0, 0, size, size);
      ctx.fillStyle = "#f7f1ee";
      ctx.fillRect(0, 0, size, size);
      if (framed) {
        const g = woodMockupLayout(mm, size);
        canvas.dataset.artworkRect = JSON.stringify([g.x / size, g.y / size, g.w / size, g.h / size]);
        ctx.drawImage(artwork2, g.x, g.y, g.w, g.h);
        drawWoodOverlay(ctx, frame, g);
      } else {
        const scale = Math.min(size * 0.8 / mm[0], size * 0.88 / mm[1]), w = mm[0] * scale, h = mm[1] * scale;
        canvas.dataset.artworkRect = JSON.stringify([(size - w) / 2 / size, (size - h) / 2 / size, w / size, h / size]);
        ctx.save();
        ctx.shadowColor = "#0004";
        ctx.shadowBlur = size * 0.016;
        ctx.shadowOffsetX = -size * 0.013;
        ctx.shadowOffsetY = size * 0.012;
        ctx.drawImage(artwork2, (size - w) / 2, (size - h) / 2, w, h);
        ctx.restore();
      }
      const label = { "natural-wood": "natural wood", "dark-wood": "dark wood", black: "black", white: "white" }[finish];
      canvas.setAttribute("aria-label", framed ? `Your personalised artwork in the supplied ${label} mockup. Framed example; ${frameLabel}.` : "Your personalised unframed print.");
      canvas.setAttribute("aria-busy", "false");
      onRender();
    };
  }

  // personalisation-product-integration/frame-review-prices.mjs
  var FRAME_REVIEW_UPGRADES = { "210x297": 40, "297x420": 50, "420x594": 80, "594x841": 120, "270x350": 50, "300x400": 50, "400x500": 80, "400x600": 80, "500x700": 90, "600x800": 120, "600x900": 130, "700x1000": 160 };
  function frameReviewPrice(model, printPriceText) {
    if (model?.country !== "GB") return null;
    const match2 = String(printPriceText).trim().match(/^£([\d,]+(?:\.\d{1,2})?)$/);
    const upgrade = FRAME_REVIEW_UPGRADES[model.mm.map((n) => n === 595 ? 594 : n).join("x")];
    if (!match2 || !upgrade) return null;
    const print = Number(match2[1].replaceAll(",", ""));
    return Number.isFinite(print) ? { print, upgrade, total: print + upgrade } : null;
  }

  // personalisation-product-integration/standard-frame-choice.mjs
  function createFramePicture(container, { mockupUrl } = {}) {
    const canvas = document.createElement("canvas");
    canvas.width = 1200;
    canvas.height = 1200;
    canvas.className = "laboo-selected-frame-canvas";
    canvas.setAttribute("role", "img");
    container.replaceChildren(canvas);
    const draw = createCleanFrameMockup(canvas, mockupUrl, { frameLabel: "frame optional" });
    let key;
    return { update(model, finish = "none", url) {
      if (!model || !url) return;
      const next = JSON.stringify([url, model.mm, finish]);
      if (next === key) return;
      key = next;
      canvas.dataset.finish = finish;
      draw(url, model.mm, finish).catch(() => {
        key = null;
        canvas.setAttribute("aria-label", "The selected finish preview could not load. Please reload the page.");
      });
    } };
  }
  function createStandardFrameChoice(pane, { onChange = () => {
  }, mockupUrl, compact = false, reviewOnly = true, checkoutEnabled = false, getOffer = () => null, initialFinish = "none", currency = "GBP" } = {}) {
    const money = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(n);
    pane.classList.add("laboo-frame-step");
    pane.innerHTML = `<div class="laboo-frame-controls">${compact ? "" : '<h2 tabindex="-1">Make it ready to hang</h2><p class="laboo-step-intro">Keep your print unframed, or add a slim wooden frame.</p>'}<fieldset class="laboo-frame-options"><legend>Finish</legend>${[["none", "Print only"], ...FRAME_COLOURS].map(([value, label]) => `<label class="laboo-frame-option"><input type="radio" name="laboo_frame_review" value="${value}"${value === "none" ? " checked" : ""}><span class="laboo-frame-swatch" data-finish="${value}" aria-hidden="true"></span><span><strong>${label}</strong><small data-frame-extra="${value}">${value === "none" ? "No frame" : "Standard slim wood frame"}</small></span></label>`).join("")}</fieldset><p class="laboo-frame-availability" role="status" aria-live="polite"></p><p class="laboo-frame-spec">Slim wooden frame \xB7 Protective acrylic glazing \xB7 Ready to hang</p><div class="laboo-frame-price-summary" aria-live="polite" data-frame-price-summary></div><p class="laboo-frame-review-note"><strong>Draft for review.</strong> Frame prices are proposals. Checkout is disabled in this preview.</p></div><figure class="laboo-frame-review-figure"><div class="laboo-frame-square" data-frame-picture></div><figcaption data-frame-caption></figcaption><p class="laboo-frame-geometry-note">Illustrative standard frame. Approximately 4\u20135 mm of artwork is covered at each edge.</p></figure>`;
    if (!reviewOnly) pane.querySelector(".laboo-frame-review-note").textContent = checkoutEnabled ? "Premium matte paper \xB7 Slim pine frame \xB7 Arrives assembled and ready to hang." : "Frame preview. Framed ordering is not available yet. Choose Print only to order.";
    const details = document.createElement("details");
    details.className = "laboo-frame-inclusions";
    details.innerHTML = "<summary>What\u2019s included with a frame?</summary><ul><li>Your personalised print on smooth, heavyweight 200 gsm matte paper.</li><li>A slim pine frame in your selected finish, with protective clear acrylic glazing (plexiglass).</li><li>The print fitted inside the frame, with backing and a hanging kit included.</li><li>Arrives assembled and ready to hang. Fittings may vary by size and production location.</li></ul><p>Print-only orders are supplied unframed. Framed orders have no separate paper mount.</p>";
    pane.querySelector(".laboo-frame-controls").append(details);
    const figure = pane.querySelector("figure"), caption = figure.querySelector("[data-frame-caption]"), geometry = figure.querySelector(".laboo-frame-geometry-note");
    const spec = pane.querySelector(".laboo-frame-spec"), availability = pane.querySelector(".laboo-frame-availability"), summary = pane.querySelector("[data-frame-price-summary]");
    const picture = createFramePicture(pane.querySelector("[data-frame-picture]"), { mockupUrl });
    let model = null, finish = initialFinish, artwork2 = null, priceText = "";
    pane.querySelector(`input[value="${initialFinish}"]`).checked = true;
    function render() {
      picture.update(model, finish, artwork2);
      caption.textContent = `${model?.label || ""} \xB7 ${finish === "none" ? "Print only" : FRAME_COLOURS.find((c) => c[0] === finish)?.[1] + " standard frame"}`;
      spec.hidden = geometry.hidden = finish === "none";
      const proposal = frameReviewPrice(model, priceText), eligible = frameEligibility(model).eligible;
      if (checkoutEnabled) pane.querySelector(".laboo-frame-review-note").textContent = finish === "none" ? eligible ? "Premium matte paper. Frame not included unless selected above." : "Premium matte paper. This size is supplied unframed." : "Premium matte paper \xB7 Slim pine frame \xB7 Arrives assembled and ready to hang.";
      for (const note of pane.querySelectorAll("[data-frame-extra]")) {
        const offer2 = getOffer(note.dataset.frameExtra);
        note.textContent = note.dataset.frameExtra === "none" ? "No frame" : !eligible ? "Print only at this size" : checkoutEnabled ? offer2 ? `+${money(offer2.upgradePence / 100)} \xB7 Ready to hang` : "Not available for this size or destination" : proposal ? `Preview +${money(proposal.upgrade)}` : "Frame price to be confirmed";
      }
      summary.replaceChildren();
      const line = (label, value, strong = false) => {
        const row = document.createElement("p");
        const name = document.createElement("span"), amount = document.createElement(strong ? "strong" : "span");
        name.textContent = label;
        amount.textContent = value;
        row.append(name, amount);
        summary.append(row);
      };
      line("Print", priceText || "Select a size");
      const offer = getOffer(finish);
      if (finish !== "none" && checkoutEnabled && offer) {
        line("Frame upgrade", "+ " + money(offer.upgradePence / 100));
        line("Print & frame total", money(offer.pricePence / 100), true);
        const note = document.createElement("small");
        note.textContent = model?.country === "GB" ? "Ready to hang \xB7 Free UK delivery." : "Ready to hang \xB7 Delivery calculated at checkout.";
        summary.append(note);
      } else if (finish !== "none" && proposal) {
        line("Frame upgrade (preview)", "+ " + money(proposal.upgrade));
        line("Preview total", money(proposal.total), true);
      } else if (finish !== "none") line("Frame upgrade", "Price to be confirmed");
      else if (model?.country === "GB") {
        const note = document.createElement("small");
        note.textContent = "UK delivery \xA35; free from \xA350.";
        summary.append(note);
      }
    }
    pane.addEventListener("change", (event) => {
      if (event.target.name !== "laboo_frame_review") return;
      finish = event.target.value;
      render();
      onChange();
    });
    return {
      figure,
      getFinish: () => finish,
      update(next, url, nextPrice = "") {
        model = next;
        priceText = nextPrice;
        if (url) artwork2 = url;
        const eligible = frameEligibility(model);
        for (const radio of pane.querySelectorAll("input")) if (radio.value !== "none") radio.disabled = !eligible.eligible || checkoutEnabled && !getOffer(radio.value);
        if ((!eligible.eligible || checkoutEnabled && !getOffer(finish)) && finish !== "none") {
          finish = "none";
          pane.querySelector('input[value="none"]').checked = true;
          onChange();
        }
        availability.textContent = eligible.reason || (checkoutEnabled && !FRAME_COLOURS.some(([value]) => !!getOffer(value)) ? "Frame not available for this size or destination. Your print is supplied unframed." : "");
        render();
      }
    };
  }

  // personalisation-product-integration/frame-order-policy.mjs
  var printOnlyCheckoutAllowed = (reviewOnly, finish = "none", verifiedFrame = false) => !reviewOnly && (finish === "none" || verifiedFrame === true);

  // personalisation-product-integration/personalised-flow-products.mjs
  var SHARED_PREVIEW_FIRST_PRODUCT_IDS = ["10820651843931", "10820651188571", "10820645192027", "10820644536667", "10820084040027", "10820649845083", "10820649124187", "10820647780699", "10820645781851", "10820654072155", "10820650500443", "10820646502747", "10820648436059", "10820637131099", "10820654661979", "10820647125339", "10820652597595", "10820653318491", "10820655317339", "10820643881307", "10820652040539", "10820651319643", "10820645323099", "10820644634971", "10820084072795", "10820649976155", "10820649320795", "10820647944539", "10820645945691", "10820654203227", "10820650664283", "10820646633819", "10820648599899", "10820637294939", "10820654825819", "10820647223643", "10820652728667", "10820653482331", "10820655448411", "10820644012379", "10820652335451", "10820651647323", "10820645585243", "10820644929883", "10820084203867", "10820650271067", "10820649648475", "10820648206683", "10820646273371", "10820654465371", "10820650893659", "10820646863195", "10820648894811", "10820637622619", "10820655087963", "10820647551323", "10820653056347", "10820653777243", "10820655808859", "10820644340059", "10820652237147", "10820651483483", "10820645454171", "10820644798811", "10820084138331", "10820650139995", "10820649419099", "10820648042843", "10820646175067", "10820654301531", "10820650795355", "10820646797659", "10820648763739", "10820637426011", "10820654956891", "10820647420251", "10820652892507", "10820653580635", "10820655612251", "10820644208987", "10408660042075", "10409289187675", "10409351414107", "10410030203227", "10410039738715", "10410055237979", "10410124083547", "10410165371227", "10410234347867", "10410413490523", "10410423288155", "10410465558875", "10410510549339", "10410515136859", "10410521690459", "10410547315035", "10410613014875", "10410629202267", "10410672292187", "10410717741403", "10410723017051", "10410750181723", "10410771710299", "10412025545051", "10412052382043", "10412074631515", "10412092424539", "10412116148571", "10412143346011", "10412161597787", "10412172280155", "10412182864219", "10412196462939", "10412207309147", "10412679266651", "10412715639131", "10412746178907", "10412797231451", "10412833997147", "10412844482907", "10412856475995", "10412887605595", "10412905627995", "10412928565595", "10414224146779", "10414238826843", "10414248395099", "10414258192731", "10414268088667", "10414280278363", "10414291419483", "10414314520923", "10414325858651", "10414759903579", "10414793490779", "10414815215963", "10414822097243", "10414827602267", "10414832025947", "10414835761499", "10416000893275", "10416124035419", "10416131473755", "10416140452187", "10416149201243", "10416211919195", "10416213819739", "10416217686363", "10416224665947", "10416227975515", "10417618813275", "10417776099675", "10417825120603", "10417857003867", "10417870045531", "10419600523611", "10420071072091", "10420071956827", "10420072284507", "10420074119515", "10420075200859", "10420075626843", "10420076183899", "10420077003099", "10420077756763"];

  // personalisation-product-integration/psd-material-artwork-renderer.mjs
  function psdDetailEligible(key, mm) {
    if (!Array.isArray(mm) || !(mm[0] > 0 && mm[1] > 0)) return false;
    if (mm[0] === mm[1]) return key === "paper-square" || /^(frame|pack)-(natural-wood|dark-wood|black|white)-square$/.test(key);
    return mm[1] > mm[0] ? key === "paper" || /^(frame-(natural-wood|dark-wood|black|white)|pack-(natural-wood|dark-wood|black|white)-portrait)$/.test(key) : key === "paper-landscape" || /^(frame|pack)-(natural-wood|dark-wood|black|white)-landscape$/.test(key);
  }
  function psdDetailInverse(q) {
    const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = q, dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3, dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3, den = dx1 * dy2 - dx2 * dy1, g = (dx3 * dy2 - dx2 * dy3) / den, h = (dx1 * dy3 - dx3 * dy1) / den, a = x1 - x0 + g * x1, b = x3 - x0 + h * x3, c = x0, d = y1 - y0 + g * y1, e = y3 - y0 + h * y3, f = y0;
    return [e - f * h, c * h - b, b * f - c * e, f * g - d, a - c * g, c * d - a * f, d * h - e * g, b * g - a * h, a * e - b * d];
  }
  function compositePsdDetail(source, background, mask, template, warp = null) {
    const { width: w, height: h, data: bg } = background, result = new Uint8ClampedArray(bg), s = source.data, sw = source.width, sh = source.height, m = psdDetailInverse(template.quad.map(([x, y]) => [x * w / template.canvas[0], y * h / template.canvas[1]]));
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const offset = (y * w + x) * 4, alpha = mask.data[offset] / 255 * template.opacity;
      if (alpha === 0) continue;
      const X = x + 0.5, Y = y + 0.5, z = m[6] * X + m[7] * Y + m[8], uv = warp ? psdDetailWarpAt(warp, (x + 0.5) * warp.width / w - 0.5, (y + 0.5) * warp.height / h - 0.5) : [(m[0] * X + m[1] * Y + m[2]) / z, (m[3] * X + m[4] * Y + m[5]) / z], u = Math.max(0, Math.min(1, uv[0])), v = Math.max(0, Math.min(1, uv[1])), px = u * (sw - 1), py = v * (sh - 1), sx = Math.floor(px), sy = Math.floor(py), fx = px - sx, fy = py - sy, p = (sy * sw + sx) * 4, q = (sy * sw + Math.min(sx + 1, sw - 1)) * 4, r = (Math.min(sy + 1, sh - 1) * sw + sx) * 4, t = (Math.min(sy + 1, sh - 1) * sw + Math.min(sx + 1, sw - 1)) * 4, weights = [(1 - fx) * (1 - fy), fx * (1 - fy), (1 - fx) * fy, fx * fy];
      for (let c = 0; c < 3; c++) {
        const ink = s[p + c] * weights[0] + s[q + c] * weights[1] + s[r + c] * weights[2] + s[t + c] * weights[3];
        result[offset + c] = bg[offset + c] * (1 - alpha) + bg[offset + c] * ink / 255 * alpha;
      }
    }
    return result;
  }
  function decodePsdDetailWarp(image3) {
    const data = new Float32Array(image3.width * image3.height * 2);
    for (let i = 0; i < image3.width * image3.height; i++) {
      const p = i * 4;
      data[i * 2] = (image3.data[p] << 4 | image3.data[p + 2] >> 4) / 4095;
      data[i * 2 + 1] = (image3.data[p + 1] << 4 | image3.data[p + 2] & 15) / 4095;
    }
    return { width: image3.width, height: image3.height, data };
  }
  function psdDetailWarpAt(warp, x, y) {
    x = Math.max(0, Math.min(warp.width - 1, x));
    y = Math.max(0, Math.min(warp.height - 1, y));
    const sx = Math.floor(x), sy = Math.floor(y), fx = x - sx, fy = y - sy, a = (sy * warp.width + sx) * 2, b = (sy * warp.width + Math.min(sx + 1, warp.width - 1)) * 2, c = (Math.min(sy + 1, warp.height - 1) * warp.width + sx) * 2, d = (Math.min(sy + 1, warp.height - 1) * warp.width + Math.min(sx + 1, warp.width - 1)) * 2, w = [(1 - fx) * (1 - fy), fx * (1 - fy), (1 - fx) * fy, fx * fy];
    return [0, 1].map((k) => warp.data[a + k] * w[0] + warp.data[b + k] * w[1] + warp.data[c + k] * w[2] + warp.data[d + k] * w[3]);
  }
  var psdDetailWarpFields = /* @__PURE__ */ new Map();
  function psdDetailWarpField(url) {
    if (!psdDetailWarpFields.has(url)) psdDetailWarpFields.set(url, psdDetailImage(url).then((image3) => decodePsdDetailWarp(psdDetailPixels(image3, image3.naturalWidth, image3.naturalHeight))).catch((e) => {
      psdDetailWarpFields.delete(url);
      throw e;
    }));
    return psdDetailWarpFields.get(url);
  }
  var psdDetailImages = /* @__PURE__ */ new Map();
  var psdDetailManifests = /* @__PURE__ */ new Map();
  var psdDetailSources = /* @__PURE__ */ new Map();
  var psdDetailResults = /* @__PURE__ */ new Map();
  function psdDetailRemember(cache, key, value, limit) {
    cache.set(key, value);
    if (cache.size > limit) cache.delete(cache.keys().next().value);
    return value;
  }
  function psdDetailImage(url) {
    if (!psdDetailImages.has(url)) psdDetailRemember(psdDetailImages, url, new Promise((resolve, reject) => {
      const image3 = new Image();
      image3.crossOrigin = "anonymous";
      image3.onload = () => resolve(image3);
      image3.onerror = () => reject(Error("The detail image could not load."));
      image3.src = url;
    }).catch((e) => {
      psdDetailImages.delete(url);
      throw e;
    }), 20);
    return psdDetailImages.get(url);
  }
  function psdDetailSurface(w, h = w) {
    return Object.assign(document.createElement("canvas"), { width: w, height: h });
  }
  function psdDetailPixels(image3, w, h = w) {
    const c = psdDetailSurface(w, h), ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(image3, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h);
  }
  function psdDetailManifest(base, mm) {
    const url = new URL(mm[0] === mm[1] ? "laboo-tony-square-psd-20261026.json" : mm[0] > mm[1] ? "laboo-tony-horizontal-psd-20261026-v2.json" : "laboo-tony-portrait-paper-psd-20261025.json", new URL(base, "https://berahgewesjy.shop/")).href;
    if (!psdDetailManifests.has(url)) psdDetailManifests.set(url, fetch(url).then((r) => {
      if (!r.ok) throw Error("The detail template could not load.");
      return r.json();
    }).catch((e) => {
      psdDetailManifests.delete(url);
      throw e;
    }));
    return psdDetailManifests.get(url);
  }
  async function renderPsdDetail({ key, mm, artworkUrl, assetBase, size = 1e3 }) {
    if (!psdDetailEligible(key, mm)) throw Error("This Photoshop template does not match the print orientation.");
    const token = [key, mm.join("x"), artworkUrl, assetBase, size].join("|");
    if (psdDetailResults.has(token)) return psdDetailResults.get(token);
    const job = (async () => {
      const manifest = await psdDetailManifest(assetBase, mm), template = manifest.templates[key];
      if (!template) throw Error("Missing Photoshop template.");
      const base = new URL(template.base, new URL(assetBase, "https://berahgewesjy.shop/")).href, sourceKey = artworkUrl + "|" + mm.join("x");
      if (!psdDetailSources.has(sourceKey)) psdDetailRemember(psdDetailSources, sourceKey, psdDetailImage(artworkUrl).then((img) => psdDetailPixels(img, 1200, Math.round(1200 * mm[1] / mm[0]))), 3);
      const [photo, maskImage, art, warp] = await Promise.all([psdDetailImage(base), psdDetailImage(manifest.masks[template.mask]), psdDetailSources.get(sourceKey), template.warp ? psdDetailWarpField(manifest.warps[template.warp]) : null]), original = psdDetailPixels(photo, size), mask = psdDetailPixels(maskImage, size), c = psdDetailSurface(size), ctx = c.getContext("2d");
      ctx.putImageData(new ImageData(compositePsdDetail(art, original, mask, template, warp), size, size), 0, 0);
      return c.toDataURL("image/webp", 0.94);
    })().catch((e) => {
      psdDetailResults.delete(token);
      throw e;
    });
    return psdDetailRemember(psdDetailResults, token, job, 12);
  }

  // personalisation-product-integration/purchase-material-details.mjs
  var MATERIAL_FINISHES = { "natural-wood": "Natural wood", "dark-wood": "Dark wood", black: "Black", white: "White" };
  function purchaseMaterialOrientation(mm) {
    const [width, height] = Array.isArray(mm) ? mm : [];
    return width > 0 && height > 0 ? width > height ? "landscape" : width < height ? "portrait" : "square" : "";
  }
  function purchaseMaterialItems(finish = "none", mm) {
    const orientation = purchaseMaterialOrientation(mm), paper = { key: orientation === "square" ? "paper-square" : orientation === "landscape" ? "paper-landscape" : "paper", title: "200 gsm matte paper", note: "Smooth, heavyweight paper with a non-reflective finish. Paper detail illustration." };
    if (!MATERIAL_FINISHES[finish]) return [paper, { key: "flat-pack", title: "Flat-pack example", note: "Smaller prints are usually sent flat. Packaging is chosen for your print size and destination." }, { key: "tubes", title: "Tubes & triangular boxes", note: "Larger prints use protective tubes or triangular packaging. Packaging can vary by print size and destination." }];
    const label = MATERIAL_FINISHES[finish], items = [paper, { key: "frame-" + finish + (orientation === "square" ? "-square" : orientation === "landscape" ? "-landscape" : ""), title: label + " frame & Perspex", note: "Slim pine frame in " + label.toLowerCase() + " with clear protective Perspex glazing beneath the frame. Your personalised artwork is fitted inside. Frame detail illustration." }, { key: "backing-" + finish, title: "Backing & hanging kit", note: label + " frame, backing fitted and hanging kit included. Arrives assembled and ready to hang. Backing illustration; fittings may vary by size and production location." }];
    if (orientation === "portrait" || orientation === "landscape" || orientation === "square") items.push({ key: "pack-" + finish + "-" + orientation, title: "Packed ready to hang", note: "Your " + orientation + " print is fitted inside a " + label.toLowerCase() + " frame with clear Perspex glazing. Arrives assembled and ready to hang. Packaging illustration; carton and protection can vary by size and destination." });
    return items;
  }
  function purchaseMaterialUrl(base, key, thumbnail = false) {
    if (key === "paper-square" || /^(frame|pack)-(natural-wood|dark-wood|black|white)-square$/.test(key)) return new URL("laboo-tony-square-" + key + "-20261026-" + (thumbnail ? "thumb" : "base") + ".webp", new URL(base, "https://berahgewesjy.shop/")).href;
    if (key === "paper-landscape" || /^(frame|pack)-(natural-wood|dark-wood|black|white)-landscape$/.test(key)) return new URL("laboo-tony-horizontal-" + key + "-20261026-" + (thumbnail ? "thumb" : "base") + ".webp", new URL(base, "https://berahgewesjy.shop/")).href;
    return new URL("laboo-own-detail-" + (key === "paper" ? "paper-hand-v2" : key) + "-20261025" + (thumbnail ? "-thumb" : "") + ".webp", new URL(base, "https://berahgewesjy.shop/")).href;
  }
  function createPurchaseMaterialDetails({ host, dialog, assetBase, getSelection, isVisible }) {
    if (!host || !dialog || !assetBase) return { update() {
    } };
    const rail = document.createElement("section");
    rail.className = "laboo-purchase-materials";
    rail.hidden = true;
    rail.setAttribute("aria-label", "Paper, frame and packaging details");
    host.append(rail);
    const viewer = document.createElement("dialog");
    viewer.className = "laboo-material-viewer";
    viewer.setAttribute("aria-label", "Material and packaging details");
    viewer.innerHTML = '<header><strong data-material-title></strong><button type="button" data-material-close>Close \xD7</button></header><div class="laboo-material-view"><button type="button" data-material-prev aria-label="Previous detail"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 6-6 6 6 6"/></svg></button><img data-material-image alt="" decoding="async"><span data-material-loading role="status" aria-live="polite">Loading detail\u2026</span><button type="button" data-material-next aria-label="Next detail"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m10 6 6 6-6 6"/></svg></button></div><p data-material-note></p><p class="laboo-material-counter" data-material-counter></p>';
    dialog.append(viewer);
    const image3 = viewer.querySelector("[data-material-image]"), loading = viewer.querySelector("[data-material-loading]");
    let items = [], key = "", index = 0, scheduled = false, showVersion = 0;
    const close = () => {
      showVersion++;
      if (viewer.open) viewer.close();
    };
    function detail(item, size) {
      const selection = getSelection() || {}, personalised = !!(psdDetailEligible(item.key, selection.mm) && selection.artworkUrl), url = purchaseMaterialUrl(assetBase, item.key, size === 320);
      if (!personalised) return Promise.resolve({ url, personalised: false });
      return renderPsdDetail({ key: item.key, mm: selection.mm, artworkUrl: selection.artworkUrl, assetBase, size }).then((url2) => ({ url: url2, personalised: true }));
    }
    async function show(next) {
      if (!items.length) return;
      index = (next + items.length) % items.length;
      const item = items[index];
      viewer.querySelector("[data-material-title]").textContent = item.title;
      viewer.querySelector("[data-material-note]").textContent = item.note;
      viewer.querySelector("[data-material-counter]").textContent = index + 1 + " of " + items.length;
      const version = ++showVersion;
      loading.hidden = false;
      loading.textContent = "Preparing your detail\u2026";
      image3.hidden = true;
      image3.alt = item.title + " \u2014 material detail";
      if (!viewer.open) viewer.showModal();
      try {
        const result = await detail(item, 1e3);
        if (version !== showVersion || !viewer.open) return;
        image3.dataset.personalised = String(result.personalised);
        image3.onload = () => {
          if (version !== showVersion) return;
          image3.hidden = false;
          loading.hidden = true;
        };
        image3.onerror = () => {
          if (version !== showVersion) return;
          image3.hidden = true;
          loading.hidden = false;
          loading.textContent = "This detail image could not load. Close and try again.";
        };
        image3.src = result.url;
      } catch {
        if (version === showVersion) {
          loading.hidden = false;
          loading.textContent = "Your personalised detail could not load. Close and try again.";
        }
      }
    }
    viewer.querySelector("[data-material-close]").addEventListener("click", close);
    viewer.querySelector("[data-material-prev]").addEventListener("click", () => show(index - 1));
    viewer.querySelector("[data-material-next]").addEventListener("click", () => show(index + 1));
    viewer.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        event.stopPropagation();
        show(index + (event.key === "ArrowLeft" ? -1 : 1));
      }
    });
    viewer.addEventListener("cancel", (event) => event.stopPropagation());
    dialog.addEventListener("close", close);
    function update() {
      const visible = !!isVisible();
      rail.hidden = !visible;
      if (host.classList.contains("laboo-has-purchase-materials") !== visible) host.classList.toggle("laboo-has-purchase-materials", visible);
      if (!visible) {
        close();
        return;
      }
      const selection = getSelection() || {}, finish = selection.finish || "none", orientation = purchaseMaterialOrientation(selection.mm), nextKey = [finish, orientation, selection.mm?.join("x"), selection.artworkUrl || ""].join("|");
      if (nextKey === key) return;
      const reopen = viewer.open, active = items[index]?.key;
      key = nextKey;
      showVersion++;
      items = purchaseMaterialItems(finish, selection.mm);
      rail.dataset.finish = finish;
      rail.dataset.orientation = orientation;
      rail.style.setProperty("--laboo-material-count", String(items.length));
      rail.replaceChildren();
      for (const [i, item] of items.entries()) {
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("aria-label", "View " + item.title.toLowerCase());
        button.setAttribute("aria-haspopup", "dialog");
        button.dataset.materialKey = item.key;
        const photo = document.createElement("img");
        photo.alt = "";
        photo.loading = "lazy";
        photo.decoding = "async";
        photo.width = 320;
        photo.height = 240;
        photo.src = purchaseMaterialUrl(assetBase, item.key, true);
        const label = document.createElement("span");
        label.textContent = item.title;
        button.append(photo, label);
        button.addEventListener("click", () => show(i));
        rail.append(button);
        if (selection.artworkUrl && psdDetailEligible(item.key, selection.mm)) {
          const ownKey = key;
          setTimeout(() => {
            if (key !== ownKey || !photo.isConnected) return;
            void detail(item, 320).then((result) => {
              if (key === ownKey && photo.isConnected) {
                photo.src = result.url;
                photo.dataset.personalised = String(result.personalised);
              }
            }).catch(() => {
            });
          }, 0);
        }
      }
      if (reopen) void show(Math.max(0, items.findIndex((item) => item.key === active)));
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        update();
      });
    }
    new MutationObserver(schedule).observe(dialog, { subtree: true, attributes: true, attributeFilter: ["src", "data-phase", "data-pf-internal", "data-mobile-edit", "class", "open"] });
    dialog.addEventListener("change", schedule);
    dialog.addEventListener("laboo:finish-change", schedule);
    schedule();
    return { update };
  }

  // personalisation-product-integration/preview-first-pilot.mjs
  var PREVIEW_FIRST_PRODUCTS = new Set(SHARED_PREVIEW_FIRST_PRODUCT_IDS);
  var isPreviewFirstPilot = (root2) => PREVIEW_FIRST_PRODUCTS.has(String(root2?.dataset.productId));
  function setupPreviewFirstPilot({ root: root2, page, form, dialog, body, preview, panes, progress, footer, next, back, showStep, validatePrint, validationMessage, modelFor, sizeSelect, options, getFrameOffer, giftFlow }) {
    if (!isPreviewFirstPilot(root2)) return null;
    back.textContent = "\u2190 Back";
    const map = !!root2.matches("[data-laboo-native-map-fields]"), count = map ? 5 : 4, labels = map ? ["Place", "Design", "Size & finish", "Gift message", "Check"] : ["Name & number", "Size & frame", "Gift message", "Check"];
    dialog.style.setProperty("--laboo-pf-steps", String(count));
    let stage = 1, internal = 1;
    page.classList.add("laboo-preview-first-pilot");
    dialog.classList.add("laboo-preview-first");
    dialog.dataset.pfKind = map ? "map" : "football";
    dialog.classList.toggle("laboo-pf-front-only", root2.dataset.exactRoomFinishes === "false");
    const aside = document.createElement("aside");
    aside.className = "laboo-pf-preview";
    aside.setAttribute("aria-label", "Your personalised print preview");
    form.prepend(aside);
    const frames = panes[1].querySelector(".laboo-frame-step");
    if (frames) {
      aside.append(frames);
      frames.querySelector("legend").textContent = "Frame finish";
    }
    createPurchaseMaterialDetails({ host: aside, dialog, assetBase: root2.dataset.frameTextureUrl, getSelection: () => ({ finish: frames?.querySelector("input:checked")?.value || "none", mm: modelFor(sizeSelect.value)?.mm, artworkUrl: panes[1].querySelector("img")?.src }), isVisible: () => dialog.open && internal === 2 });
    const frameInclusion = document.createElement("p");
    frameInclusion.className = "laboo-pf-frame-inclusion";
    frameInclusion.setAttribute("aria-live", "polite");
    frames?.querySelector("fieldset").after(frameInclusion);
    for (const label of frames?.querySelectorAll(".laboo-frame-option") || []) {
      const price = document.createElement("small");
      price.className = "laboo-pf-mobile-frame-price";
      label.querySelector("strong").after(price);
    }
    const toolbar = document.createElement("div");
    toolbar.className = "laboo-pf-view-toolbar";
    toolbar.setAttribute("role", "group");
    toolbar.setAttribute("aria-label", "Preview view");
    for (const [label, image3] of [["Your print", 0], ["In a room", 1]]) {
      if (image3 === 1 && root2.dataset.exactRoomFinishes === "false") continue;
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = label;
      b.dataset.pfView = String(image3);
      b.onclick = () => preview.querySelector(`[data-gallery-index="${image3}"]`)?.click();
      toolbar.append(b);
    }
    preview.querySelector("[data-preview-gallery]").append(toolbar);
    const gallery = preview.querySelector("[data-preview-gallery]");
    function paintView() {
      for (const b of toolbar.children) b.setAttribute("aria-pressed", String(b.dataset.pfView === "0" ? gallery.dataset.selectedImage === "1" : gallery.dataset.selectedImage !== "1"));
    }
    new MutationObserver(paintView).observe(gallery, { attributes: true, attributeFilter: ["data-selected-image"] });
    paintView();
    const legend = root2.querySelector("legend"), status = root2.querySelector("[data-editor-status]");
    let placeGroup, designGroup;
    if (map) {
      const titleLabel = root2.querySelector('label[for="map-title"]');
      placeGroup = document.createElement("div");
      designGroup = document.createElement("div");
      placeGroup.className = "laboo-pf-place-fields";
      designGroup.className = "laboo-pf-design-fields";
      let design = false;
      for (const node of [...root2.children]) {
        if (node === titleLabel) design = true;
        if (node === legend || node === status || node.tagName === "SCRIPT") continue;
        (design ? designGroup : placeGroup).append(node);
      }
      const extra = designGroup.querySelector(".native-map-extra"), subtitle = designGroup.querySelector('[data-map-field="subtitle"]'), subtitleLabel = designGroup.querySelector('label[for="map-subtitle"]');
      if (extra && subtitle) extra.querySelector("summary").after(subtitleLabel, subtitle);
      const colours = designGroup.querySelector("[data-map-colours]"), colourLabel = designGroup.querySelector(".native-map-colour-label");
      if (colours) designGroup.prepend(colourLabel, colours);
      root2.append(placeGroup, designGroup, status);
      if (extra) extra.open = true;
    } else {
      legend.textContent = "Their name. Their number.";
    }
    const order = document.createElement("div");
    order.className = "laboo-pf-order";
    order.setAttribute("aria-live", "polite");
    order.innerHTML = "<span></span><strong></strong>";
    footer.prepend(order);
    panes[1].querySelector('label[for="laboo-mobile-size"]').textContent = "Print size";
    const sizeGuide = panes[1].querySelector(".laboo-size-frame-guide");
    if (sizeGuide) {
      sizeGuide.open = true;
      sizeGuide.querySelector(".laboo-scale-guide-note").textContent = "Illustrative comparison. Measurements are for the print; any frame shown is for scale.";
    }
    progress.replaceChildren(...labels.map((label, i) => {
      const li = document.createElement("li"), b = document.createElement("button");
      b.type = "button";
      b.textContent = label;
      b.setAttribute("aria-label", i + 1 + ". " + label);
      b.onclick = () => {
        if (i + 1 < stage) go(i + 1);
      };
      li.append(b);
      return li;
    }));
    function go(value) {
      stage = Math.max(1, Math.min(count, value));
      showStep(map ? stage <= 2 ? 1 : stage - 1 : stage);
    }
    const mobile = window.matchMedia("(max-width:767px)"), tasks = [];
    let mobileIndex = 0, optionalWords = map, mobileCurrentId = map ? "place" : "laboo_name";
    const mobileHeading = document.createElement("div");
    mobileHeading.className = "laboo-pf-mobile-heading";
    mobileHeading.innerHTML = '<strong role="heading" aria-level="2"></strong><span></span>';
    progress.after(mobileHeading);
    function part(parent, nodes) {
      const group = document.createElement("div");
      group.className = "laboo-pf-mobile-part";
      const first = nodes.find(Boolean);
      if (first) first.before(group);
      else parent.append(group);
      for (const n of nodes) if (n) group.append(n);
      return group;
    }
    function task(id, label, phase, value, group, optional = false) {
      const input = group?.querySelector("input:not([type=checkbox]):not([type=radio]):not([type=hidden]),select,textarea");
      if (input?.id) {
        for (const original of group.querySelectorAll("label[for]")) if (original.htmlFor === input.id) original.classList.add("laboo-pf-mobile-label");
      }
      tasks.push({ id, label, phase, value, group, optional });
    }
    if (map) {
      const zoom = placeGroup.querySelector(".native-map-zoom");
      task("place", "Choose your place", 1, 1, part(placeGroup, [...placeGroup.children].filter((n) => n !== zoom)));
      task("area", "Choose the map area", 1, 1, part(placeGroup, [zoom]));
      task("colour", "Choose a map colour", 2, 1, part(designGroup, [designGroup.querySelector(".native-map-colour-label"), designGroup.querySelector("[data-map-colours]")]));
      const title = part(designGroup, [designGroup.querySelector('label[for="map-title"]'), designGroup.querySelector("#map-title")]);
      task("title", "Title on your print", 2, 1, title);
      const extra = designGroup.querySelector(".native-map-extra");
      for (const [field, label] of [["subtitle", "Second line (optional)"], ["message", "Short message (optional)"], ["names", "Names (optional)"], ["date", "Date (optional)"]]) task(field, label, 2, 1, part(extra, [extra.querySelector(`label[for="map-${field}"]`), extra.querySelector(`#map-${field}`)]), true);
    } else {
      for (const [name, label] of [["laboo_name", "Add their name"], ["laboo_number", "Choose their number"]]) {
        const input = root2.querySelector(`[name="${name}"]`), group = [...root2.querySelector(".laboo-original-field-grid").children].find((n) => n.contains(input));
        group.classList.add("laboo-pf-mobile-field");
        task(name, label, 1, 1, group);
      }
    }
    const sizeControls = panes[1].querySelector(".laboo-size-frame-controls");
    const sizeGroup = part(sizeControls, [sizeControls.querySelector('label[for="laboo-mobile-size"]'), sizeSelect, sizeControls.querySelector(".laboo-size-frame-guide")]);
    task("size", "Choose your print size", map ? 3 : 2, 2, sizeGroup);
    task("frame", "Choose your frame", map ? 3 : 2, 2, frames);
    if (giftFlow) {
      task("gift-choice", "Gift message (optional)", map ? 4 : 3, 3, giftFlow.choice);
      for (const [field, label] of [["recipientName", "Who is it for?"], ["recipientEmail", "Their email address"], ["fromName", "Who is it from?"], ["message", "Write your gift message"]]) {
        const group = giftFlow.fields.querySelector(`[data-gift-part="${field}"]`);
        group.classList.add("laboo-pf-mobile-part");
        task("gift-" + field, label, map ? 4 : 3, 3, group);
        tasks.at(-1).giftField = field;
      }
    }
    task("proof", "Check your final proof", map ? 5 : 4, 4, null);
    const visibleTasks = () => tasks.filter((t) => (!t.optional || optionalWords) && (!t.giftField || giftFlow.isSelected()));
    function applyMobile() {
      const list = visibleTasks(), current = list[mobileIndex] || list[0];
      dialog.dataset.mobileEdit = current.id;
      mobileCurrentId = current.id;
      for (const t of tasks) t.group?.classList.toggle("laboo-pf-mobile-active", t === current);
      mobileHeading.querySelector("strong").textContent = current.label;
      mobileHeading.querySelector("span").textContent = `${mobileIndex + 1} / ${list.length}`;
      if (!mobile.matches) return;
      const extra = designGroup?.querySelector(".native-map-extra");
      if (extra) extra.open = true;
      if (frames) {
        if (current.id === "frame") sizeGroup.after(frames);
        else if (internal !== 2) aside.append(frames);
      }
      back.hidden = mobileIndex === 0;
      next.textContent = current.id === "gift-choice" && !giftFlow.isSelected() ? "Continue without a message \u2192" : list[mobileIndex + 1]?.id === "proof" ? "Check final proof \u2192" : list[mobileIndex + 1]?.id === "gift-choice" ? "Gift message \u2192" : list[mobileIndex + 1]?.id === "size" ? "Size & finish \u2192" : list[mobileIndex + 1]?.id === "frame" ? "Choose frame \u2192" : "Next \u2192";
      if (current.id === "title" && !optionalWords) next.textContent = "Choose size \u2192";
    }
    function moveMobile(index) {
      const list = visibleTasks();
      mobileIndex = Math.max(0, Math.min(list.length - 1, index));
      const t = list[mobileIndex];
      stage = t.phase;
      showStep(t.value);
    }
    next.addEventListener("click", (event) => {
      if (!mobile.matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const list = visibleTasks(), current = list[mobileIndex], target = list[mobileIndex + 1];
      if (!target) return;
      if (current.id === "place" && !/^Selected:/.test(root2.querySelector("[data-map-selected-location]").textContent)) {
        status.textContent = "Choose an address, or use the city centre, before continuing.";
        return;
      }
      if (current.id === "title" || current.id === "laboo_name") {
        const input = root2.querySelector(current.id === "title" ? "#map-title" : '[name="laboo_name"]');
        if (!input.value.trim()) {
          status.textContent = current.id === "title" ? "Enter a title for your print." : "Enter the name to print on the shirt.";
          input.focus({ preventScroll: true });
          return;
        }
      }
      if ((target.id === "proof" || !map && current.id === "laboo_number") && !validatePrint?.()) {
        status.textContent = validationMessage;
        return;
      }
      if (target.id === "proof" && !options.some((o) => o.dataset.pVariantId === sizeSelect.value && o.dataset.pAvailable === "true")) {
        status.textContent = "Choose an available print size before continuing.";
        return;
      }
      if (current.giftField && !giftFlow.validate(current.giftField)) return;
      if (target.id === "proof" && !giftFlow?.validate()) return;
      moveMobile(mobileIndex + 1);
    }, true);
    back.addEventListener("click", (event) => {
      if (!mobile.matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      moveMobile(mobileIndex - 1);
    }, true);
    const enlarge = gallery.querySelector(".laboo-gallery-enlarge"), originalEnlarge = enlarge?.textContent;
    function responsive() {
      dialog.classList.remove("laboo-pf-compact", "laboo-pf-keyboard");
      if (enlarge) {
        enlarge.textContent = mobile.matches ? "Full screen" : originalEnlarge;
        enlarge.setAttribute("aria-label", mobile.matches ? "View preview full screen" : "Enlarge preview image");
      }
      if (mobile.matches) {
        const list = visibleTasks();
        mobileIndex = Math.max(0, list.findIndex((t) => t.phase === stage));
        moveMobile(mobileIndex);
      } else {
        const extra = designGroup?.querySelector(".native-map-extra");
        if (extra) extra.open = true;
        showStep(internal);
      }
    }
    mobile.addEventListener("change", responsive);
    next.addEventListener("click", (event) => {
      if (map && internal === 1 && stage === 1) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!validatePrint?.()) {
          status.textContent = validationMessage;
          return;
        }
        go(2);
      }
    }, true);
    if (map) back.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      go(stage - 1);
    }, true);
    if (enlarge && mobile.matches) {
      enlarge.textContent = "Full screen";
      enlarge.setAttribute("aria-label", "View preview full screen");
    }
    return {
      giftAvailabilityChanged() {
        if (mobile.matches) {
          const list = visibleTasks();
          mobileIndex = Math.max(0, list.findIndex((t) => t.id === mobileCurrentId));
          if (list[mobileIndex].value !== internal) moveMobile(mobileIndex);
          else applyMobile();
        } else if (internal === 3) next.textContent = giftFlow.isSelected() ? "Check final proof \u2192" : "Continue without a message \u2192";
      },
      mountPreview() {
        aside.prepend(preview);
      },
      onStep(value) {
        internal = value;
        if (map) {
          if (value > 1) stage = value + 1;
          else if (stage > 2) stage = 2;
        } else stage = value;
        if (frames) {
          const ordering = value === 2;
          if (ordering) sizeSelect.after(frames);
          else aside.append(frames);
          frames.classList.toggle("laboo-pf-order-frames", ordering);
          frames.querySelector("legend").textContent = ordering ? "Frame" : "Frame finish";
        }
        dialog.dataset.pfStage = String(stage);
        dialog.dataset.pfInternal = String(value);
        aside.hidden = value === 4;
        dialog.classList.toggle("laboo-pf-proof", value === 4);
        dialog.classList.remove("laboo-pf-compact", "laboo-pf-keyboard");
        if (mobile.matches) {
          const t = visibleTasks()[mobileIndex];
          if (t?.value !== value) {
            mobileIndex = Math.max(0, visibleTasks().findIndex((t2) => t2.value === value));
          }
          stage = visibleTasks()[mobileIndex].phase;
        }
        if (map) {
          placeGroup.hidden = stage !== 1;
          designGroup.hidden = stage !== 2;
          legend.textContent = stage === 1 ? "Which place is yours?" : "Make it feel like you.";
        }
        for (const [i, li] of [...progress.children].entries()) {
          li.toggleAttribute("aria-current", i + 1 === stage);
          if (i + 1 === stage) li.setAttribute("aria-current", "step");
          const b = li.querySelector("button");
          b.disabled = i + 1 > stage;
          b.setAttribute("aria-current", i + 1 === stage ? "step" : "false");
        }
        footer.querySelector(".laboo-customiser-step-label").textContent = `Step ${stage} of ${count}`;
        back.hidden = stage === 1;
        if (map && stage === 1) next.textContent = "Choose your design \u2192";
        else if (value === 1) next.textContent = "Choose size & frame \u2192";
        applyMobile();
        panes[value - 1].scrollTop = 0;
        if (dialog.open && !mobile.matches) progress.children[stage - 1].querySelector("button").focus({ preventScroll: true });
      },
      refresh({ model, price, offer, finish }) {
        const label = finish === "none" ? "Print only" : { "natural-wood": "Natural wood", "dark-wood": "Dark wood", black: "Black", white: "White" }[finish];
        frameInclusion.textContent = finish === "none" ? "You will receive an unframed paper print. No frame is included." : "Your print is fitted in a " + label.toLowerCase() + " frame with clear Perspex glazing. Arrives assembled and ready to hang.";
        order.querySelector("span").textContent = (model?.label || "Choose a size") + " \xB7 " + label;
        order.querySelector("strong").textContent = offer ? new Intl.NumberFormat("en-GB", { style: "currency", currency: offer.currency || root2.dataset.currency || "GBP" }).format(offer.pricePence / 100) : price;
        for (const card of frames?.querySelectorAll(".laboo-frame-option") || []) {
          const value = card.querySelector("input").value, upgrade = getFrameOffer?.(sizeSelect.value, value);
          card.querySelector(".laboo-pf-mobile-frame-price").textContent = value === "none" ? "No frame" : upgrade ? "+" + new Intl.NumberFormat("en-GB", { style: "currency", currency: upgrade.currency || root2.dataset.currency || "GBP" }).format(upgrade.upgradePence / 100) : "Unavailable";
        }
        for (const option of sizeSelect.options) {
          const m = modelFor(option.value), node = options.find((o) => o.dataset.pVariantId === option.value), frameOffer = getFrameOffer?.(option.value, finish);
          if (m && node) {
            const amount = frameOffer ? new Intl.NumberFormat("en-GB", { style: "currency", currency: frameOffer.currency || root2.dataset.currency || "GBP" }).format(frameOffer.pricePence / 100) : node.dataset.pPrice;
            option.textContent = m.label + " \xB7 " + amount + (finish !== "none" && !frameOffer ? " \xB7 Print only" : "");
          }
        }
      }
    };
  }

  // personalisation-product-integration/mobile-layout.mjs
  function setupMobileLayout(root2, { validatePrint, prepareProof, onChange = () => {
  }, onOpen = () => {
  }, variants = {}, getFrameOffer = () => null, initialFinish = "none", describePrint, getSizeModel, validationMessage = "Enter a name and shirt number, then wait for your preview.", returnsText, proofType } = {}) {
    const frameOptions = root2.dataset.frameOptions === "true" || root2.dataset.frameReview === "true";
    const framesReview = frameOptions && root2.dataset.launchMode !== "live";
    const frameCheckout = root2.dataset.frameCheckout === "true";
    const hasGiftStep = isPreviewFirstPilot(root2), lastStep = hasGiftStep ? 4 : 3;
    const page = root2.closest("product-page"), form = root2.closest("form"), host = form?.parentElement;
    const info = page?.querySelector(".product-info-col"), preview = page?.querySelector(".laboo-original-preview"), cart = form?.querySelector(".product__cart-functions");
    if (!page || !info || !preview || !cart) return;
    const notes = [preview.querySelector(".laboo-preview-build-note"), preview.querySelector(".laboo-original-example-note")].filter(Boolean);
    const media = window.matchMedia("(max-width:767px)");
    const cover = document.createElement("div");
    cover.className = "laboo-mobile-cover";
    cover.innerHTML = '<button type="button" class="laboo-customiser-launch">Personalise your print</button>';
    const dialog = document.createElement("dialog");
    dialog.className = "laboo-mobile-customiser";
    dialog.setAttribute("aria-label", "Personalise your print");
    if (root2.dataset.frameTextureUrl) dialog.style.setProperty("--laboo-frame-texture", `url("${root2.dataset.frameTextureUrl}")`);
    dialog.innerHTML = '<header class="laboo-customiser-header"><strong>Personalise your print</strong><button type="button" data-customiser-close aria-label="Close personalisation">Close \xD7</button></header>';
    host.append(cover, dialog);
    const desktopCover = document.createElement("div");
    desktopCover.className = "laboo-desktop-cover";
    preview.before(desktopCover);
    const galleryWishlist = page.querySelector(".p-media-col .laboo-product-gallery-wishlist");
    if (galleryWishlist) desktopCover.append(galleryWishlist);
    const galleryHome = document.createComment("Product gallery home");
    desktopCover.before(galleryHome);
    const body = document.createElement("div");
    body.className = "laboo-customiser-body";
    const progress = document.createElement("ol");
    progress.className = "laboo-customiser-progress";
    progress.setAttribute("aria-label", "Personalisation steps");
    progress.innerHTML = frameOptions ? "<li>1 \xB7 Personalise</li><li>2 \xB7 Size &amp; frame</li><li>3 \xB7 Final proof</li>" : "<li>1 \xB7 Personalise</li><li>2 \xB7 Size</li><li>3 \xB7 Final proof</li>";
    body.append(progress);
    const panes = Array.from({ length: lastStep }, (_, i) => {
      const pane = document.createElement("section");
      pane.className = "laboo-customiser-step";
      pane.dataset.step = String(i + 1);
      body.append(pane);
      return pane;
    });
    panes[1].innerHTML = '<h2 tabindex="-1">Choose your print size</h2><p class="laboo-step-intro">Select a size to update your print and price.</p><label for="laboo-mobile-size">Print size</label><select id="laboo-mobile-size" aria-label="Print size"></select><div class="laboo-size-review"><img alt="Your selected print" width="150" height="200"><p data-size-summary></p></div>';
    panes[lastStep - 1].innerHTML = '<p class="laboo-order-summary"></p>';
    if (hasGiftStep) {
      panes[2].classList.add("laboo-gift-step");
      panes[2].innerHTML = '<h2 tabindex="-1">Gift message</h2><p class="laboo-step-intro">Add an optional note for the recipient. We\u2019ll email it after the courier confirms delivery, separately from your printed artwork.</p>';
    }
    const returnsNote = document.createElement("p");
    returnsNote.className = "laboo-personalised-returns";
    returnsNote.textContent = returnsText || "Made just for you. No change-of-mind returns or refunds. Your rights if your item is damaged, faulty or not as described are unaffected. Please check your name, number and size before adding to your basket.";
    panes[lastStep - 1].append(returnsNote);
    const footer = document.createElement("div");
    footer.className = "laboo-customiser-footer";
    footer.innerHTML = '<p class="laboo-customiser-step-label" aria-live="polite"></p><p data-mobile-save-status role="status" aria-live="polite"></p><div class="laboo-customiser-navigation"><button type="button" data-customiser-back>Back</button><button type="button" data-customiser-next>Choose size \u2192</button></div><p class="laboo-customiser-notice">Preview only \xB7 Checkout and gift emails disabled</p>';
    const back = footer.querySelector("[data-customiser-back]"), next = footer.querySelector("[data-customiser-next]"), sizeSelect = panes[1].querySelector("select");
    const options = [...page.querySelectorAll(".p-variants-sidebar-wrap .single-variant")].filter((node) => !node.hidden && Object.hasOwn(variants, node.dataset.pVariantId));
    for (const node of options) {
      const option = document.createElement("option");
      option.value = node.dataset.pVariantId;
      option.textContent = `${node.dataset.pVariantLabel || node.dataset.pVariant} \u2014 ${node.dataset.pPrice}`;
      sizeSelect.append(option);
    }
    const sizeCountry = root2.dataset.country || document.documentElement.dataset.labooCountry || "GB";
    const modelFor = (id) => getSizeModel ? getSizeModel(id) : selectedSize(id, sizeCountry, options.find((node) => node.dataset.pVariantId === id)?.dataset.pVariantLabel || "", variants);
    for (const option of sizeSelect.options) {
      const model = modelFor(option.value), node = options.find((node2) => node2.dataset.pVariantId === option.value);
      if (model) {
        const canFrame = frameEligibility(model).eligible && (!frameCheckout || FRAME_COLOURS.some(([finish]) => !!getFrameOffer(option.value, finish)));
        option.textContent = `${model.label} \u2014 ${node.dataset.pPrice}${node.dataset.pAvailable === "false" ? " \xB7 Unavailable" : frameOptions && !canFrame ? " \xB7 Print only (frame not available)" : ""}`;
      } else {
        option.disabled = true;
        option.textContent = "A0 \xB7 Unavailable for this delivery country";
      }
    }
    const availabilityNote = document.createElement("p");
    availabilityNote.setAttribute("role", "status");
    availabilityNote.hidden = true;
    sizeSelect.after(availabilityNote);
    const available = () => !!modelFor(form.querySelector('[name="id"]')?.value) && options.some((node) => node.dataset.pVariantId === form.querySelector('[name="id"]')?.value && node.dataset.pAvailable === "true");
    const guideContainer = document.createElement("section");
    guideContainer.className = "laboo-selected-scale-guide";
    panes[1].append(guideContainer);
    const scaleGuide = createSelectedSizeGuide(guideContainer);
    let artworkUrl;
    let frameChoice = null;
    if (frameOptions) {
      panes[1].classList.add("laboo-size-frame-step");
      panes[1].querySelector("h2").textContent = "Choose your size & frame";
      panes[1].querySelector(".laboo-step-intro").textContent = "Choose a print size, then keep it unframed or add a slim wooden frame.";
      panes[1].querySelector(".laboo-size-review").hidden = true;
      const controls = document.createElement("div");
      controls.className = "laboo-size-frame-controls";
      while (panes[1].firstChild) controls.append(panes[1].firstChild);
      panes[1].append(controls);
      const host2 = document.createElement("div");
      controls.append(host2);
      frameChoice = createStandardFrameChoice(host2, { compact: true, reviewOnly: framesReview, checkoutEnabled: frameCheckout, initialFinish, currency: root2.dataset.currency || window.Shopify?.currency?.active || "GBP", getOffer: (finish) => getFrameOffer(form.querySelector('[name="id"]')?.value, finish), mockupUrl: root2.dataset.frameTextureUrl, onChange: () => {
        finalProof.invalidate();
        refresh();
        root2.dispatchEvent(new CustomEvent("laboo:finish-change"));
        onChange();
      } });
      panes[1].append(frameChoice.figure);
      const guide = document.createElement("details");
      guide.className = "laboo-size-frame-guide";
      guide.innerHTML = "<summary>See the size beside a person</summary>";
      guide.append(guideContainer);
      controls.append(guide);
    }
    const frameDraft = document.createElement("section");
    frameDraft.className = "laboo-customiser-step laboo-frame-draft-proof";
    frameDraft.hidden = true;
    frameDraft.innerHTML = '<div><h2 tabindex="-1">Review your framed print</h2><p data-frame-draft-summary></p><p>This shows the selected frame around your personalised artwork.</p><p class="laboo-frame-review-note"><strong>Framing preview only.</strong> Pricing, saved frame approval and production are still being connected. This is not an approved production proof.</p><p>Frame appearance and the approximate edge coverage are illustrative.</p></div><div class="laboo-frame-square" data-frame-draft-picture></div>';
    const frameDraftPicture = frameOptions ? createFramePicture(frameDraft.querySelector("[data-frame-draft-picture]"), { mockupUrl: root2.dataset.frameTextureUrl }) : null;
    if (frameOptions) body.append(frameDraft);
    const framePending = document.createElement("button");
    framePending.type = "button";
    framePending.disabled = true;
    framePending.dataset.frameCheckoutPending = "";
    framePending.textContent = "Framing preview \xB7 Checkout disabled";
    framePending.hidden = true;
    footer.querySelector(".laboo-customiser-notice").before(framePending);
    let step = 1, pilot = null;
    const editorStatus = root2.querySelector("[data-editor-status]");
    footer.querySelector(".laboo-customiser-notice").textContent = "Preview only \xB7 Checkout disabled";
    if (root2.dataset.launchMode === "live") footer.querySelector(".laboo-customiser-notice").textContent = "Personalised print \xB7 Frame not included";
    if (framesReview) footer.querySelector(".laboo-customiser-notice").textContent = "Frame design preview \xB7 Checkout disabled";
    const syncStatus = () => {
      footer.querySelector("[data-mobile-save-status]").textContent = step === lastStep ? editorStatus.textContent : "";
    };
    new MutationObserver(syncStatus).observe(editorStatus, { childList: true, characterData: true, subtree: true });
    const getGift = () => giftFlow.getGift();
    function refresh() {
      sizeSelect.value = form.querySelector('[name="id"]')?.value || "";
      const model = modelFor(sizeSelect.value), size = model?.label || "", price = options.find((node) => node.dataset.pVariantId === sizeSelect.value)?.dataset.pPrice || "";
      const description = describePrint ? describePrint() : `${root2.querySelector('[name="laboo_name"]').value.toUpperCase()} / ${root2.querySelector('[name="laboo_number"]').value}`;
      scaleGuide.update(model, frameOptions ? frameChoice.getFinish() : void 0);
      guideContainer.hidden = !model;
      availabilityNote.hidden = available();
      availabilityNote.textContent = available() ? "" : "This size is currently unavailable for your location. You can preview it, but cannot add it to your basket.";
      panes[1].querySelector("[data-size-summary]").textContent = `${size} \xB7 ${price}`;
      frameChoice?.update(model, artworkUrl, price);
      const checkoutOffer = frameCheckout ? getFrameOffer(sizeSelect.value, frameChoice?.getFinish()) : null, cartPrice = cart.querySelector(".addtocart-btn-price");
      if (cartPrice) cartPrice.textContent = " . " + (checkoutOffer ? new Intl.NumberFormat("en-GB", { style: "currency", currency: checkoutOffer.currency || root2.dataset.currency || window.Shopify?.currency?.active || "GBP" }).format(checkoutOffer.pricePence / 100) : price);
      const summary = panes[lastStep - 1].querySelector(".laboo-order-summary");
      if (summary) summary.textContent = `${description} \xB7 ${size} \xB7 ${price}`;
      if (frameOptions) {
        const finish = frameChoice.getFinish();
        frameDraftPicture.update(model, finish, artworkUrl);
        frameDraft.querySelector("[data-frame-draft-summary]").textContent = `${description} \xB7 ${size} \xB7 ${FRAME_COLOURS.find((c) => c[0] === finish)?.[1] || "Print only"}${finish === "none" ? "" : " standard frame"}`;
      }
      if (frameOptions && !framesReview) footer.querySelector(".laboo-customiser-notice").textContent = frameChoice.getFinish() === "none" ? "Print only \xB7 Frame not included" : frameCheckout ? "Print & frame \xB7 Assembled and ready to hang \xB7 " + (sizeCountry === "GB" ? "Free UK delivery" : "Delivery calculated at checkout") : "Frame preview \xB7 Ordering not yet available";
      pilot?.refresh({ model, price, offer: checkoutOffer, finish: frameChoice?.getFinish() || "none" });
    }
    function showStep(value) {
      step = value;
      panes.forEach((pane, i) => pane.hidden = i !== step - 1);
      [...progress.children].forEach((li, i) => {
        if (i === step - 1) li.setAttribute("aria-current", "step");
        else li.removeAttribute("aria-current");
      });
      footer.querySelector(".laboo-customiser-step-label").textContent = `Step ${step} of ${lastStep}`;
      back.hidden = step === 1;
      next.hidden = step === lastStep;
      cart.hidden = step !== lastStep;
      next.textContent = step === 1 ? frameOptions ? "Choose size & frame \u2192" : "Choose size \u2192" : hasGiftStep && step === 2 ? "Gift message \u2192" : hasGiftStep && step === 3 && !giftFlow.isSelected() ? "Continue without a message \u2192" : "Check final proof \u2192";
      refresh();
      const framed = frameOptions && step === lastStep && frameChoice.getFinish() !== "none" && !frameCheckout;
      frameDraft.hidden = !framed;
      framePending.hidden = !framed;
      if (framed) {
        panes[lastStep - 1].hidden = true;
        cart.hidden = true;
      }
      syncStatus();
      body.scrollTop = 0;
      if (step === lastStep && !framed) finalProof.prepare();
      else finalProof.invalidate();
      onChange();
      if (dialog.open && !pilot) ((framed ? frameDraft : panes[step - 1]).querySelector("h2,input") || panes[step - 1]).focus({ preventScroll: true });
      pilot?.onStep(step);
    }
    function open() {
      if (!available()) options.find((node) => node.dataset.pAvailable === "true" && modelFor(node.dataset.pVariantId))?.click();
      if (pilot) pilot.mountPreview();
      else panes[0].append(preview);
      dialog.showModal();
      showStep(step);
      onOpen();
    }
    cover.querySelector(".laboo-customiser-launch").addEventListener("click", open);
    dialog.addEventListener("close", () => {
      desktopCover.append(preview);
      onOpen();
    });
    dialog.querySelector("[data-customiser-close]").addEventListener("click", () => dialog.close());
    back.addEventListener("click", () => showStep(Math.max(1, step - 1)));
    function advance() {
      if (step === 2 && !available()) {
        availabilityNote.hidden = false;
        sizeSelect.focus();
        return;
      }
      if (step === 2 && !validatePrint?.()) {
        availabilityNote.hidden = false;
        availabilityNote.textContent = "Please wait for your updated size preview, then continue.";
        return;
      }
      if (step === 1 && !validatePrint?.()) {
        root2.querySelector("[data-editor-status]").textContent = validationMessage;
        return;
      }
      if (step === lastStep - 1 && !giftFlow.validate()) return;
      showStep(Math.min(lastStep, step + 1));
    }
    next.addEventListener("click", advance);
    sizeSelect.addEventListener("change", () => {
      options.find((node) => node.dataset.pVariantId === sizeSelect.value)?.click();
      refresh();
      onChange();
    });
    const giftFlow = createGiftMessage(hasGiftStep ? panes[2] : panes[1].querySelector(".laboo-size-frame-controls") || panes[1], { productId: root2.dataset.productId, api: root2.dataset.api || "https://laboo-personalisation-checkout-pilot.laboo-api.workers.dev", onChange: () => {
      finalProof.invalidate();
      pilot?.giftAvailabilityChanged();
      onChange();
    }, onAvailability: () => {
      pilot?.giftAvailabilityChanged();
    } });
    if (hasGiftStep) giftFlow.ready.then((enabled) => {
      if (!enabled) panes[2].querySelector(".laboo-step-intro").textContent = "Gift messages are temporarily unavailable. Continue to check your print without a message.";
    });
    const finalProof = createFinalProofPanel(panes[lastStep - 1], { prepareProof, onChange, isActive: () => step === lastStep && (!frameChoice || frameChoice.getFinish() === "none" || frameCheckout), country: sizeCountry, rendererUrl: root2.dataset.pdfRendererUrl, workerUrl: root2.dataset.pdfWorkerUrl, personalisationType: proofType, mobileApprovalPrompt: isPreviewFirstPilot(root2) });
    panes[lastStep - 1].querySelector(".laboo-final-proof-details").append(returnsNote);
    page.classList.add("laboo-step-layout");
    panes[0].append(root2);
    desktopCover.append(preview);
    footer.querySelector(".laboo-customiser-navigation").after(cart);
    form.append(body, footer);
    dialog.append(form);
    for (const note of notes) note.hidden = true;
    pilot = setupPreviewFirstPilot({ root: root2, page, form, dialog, body, preview, panes, progress, footer, next, back, showStep, validatePrint, validationMessage, modelFor, sizeSelect, options, getFrameOffer, giftFlow });
    function apply() {
      page.classList.toggle("laboo-mobile-layout", media.matches);
      if (media.matches) cover.prepend(desktopCover);
      else galleryHome.after(desktopCover);
    }
    apply();
    media.addEventListener("change", apply);
    showStep(step);
    return {
      getSelection() {
        const finish = frameChoice?.getFinish() || "none", offer = getFrameOffer(form.querySelector('[name="id"]')?.value, finish);
        return frameCheckout && offer ? { finish, purchaseVariantId: offer.purchaseVariantId } : {};
      },
      getGift,
      getGiftKey: () => giftFlow.getGiftKey(),
      getProof: () => finalProof.getProof(),
      invalidateProof: () => finalProof.invalidate(),
      // On mobile a ready proof keeps Add to Cart actionable. beforeSubmit still
      // requires explicit approval before any approval request or cart mutation.
      canSubmit: () => printOnlyCheckoutAllowed(framesReview, frameChoice?.getFinish(), frameCheckout && !!getFrameOffer(form.querySelector('[name="id"]')?.value, frameChoice?.getFinish())) && step === lastStep && available() && (pilot && media.matches ? finalProof.isReady() : finalProof.isApproved()),
      beforeSubmit() {
        if (!printOnlyCheckoutAllowed(framesReview, frameChoice?.getFinish(), frameCheckout && !!getFrameOffer(form.querySelector('[name="id"]')?.value, frameChoice?.getFinish()))) {
          editorStatus.textContent = "This frame is not available for checkout. Please choose Print only.";
          return false;
        }
        if (step !== lastStep) {
          advance();
          return false;
        }
        if (!available()) {
          editorStatus.textContent = "This size is currently unavailable for your location. Please choose another size.";
          return false;
        }
        return finalProof.validate();
      },
      setPreview(url, alt) {
        artworkUrl = url;
        panes[1].querySelector("img").src = url;
        scaleGuide.setArtwork(url);
        refresh();
      }
    };
  }

  // personalisation-product-integration/print-layout.mjs
  var PRINT_REVISION = 8;

  // personalisation-product-integration/room-frame-finishes.mjs
  var corrections = {
    "yellow/living-room": ["10417857003867", "laboo-room-yellow-living-room-black-a80593eaedb9.webp"],
    "green/snug": ["10414314520923", "laboo-room-green-snug-black-ed98239698d2.webp"],
    "black/living-room": ["10417618813275", "laboo-room-black-living-room-black-ac80dfcc86c2.webp"],
    "maroon/snug": ["10412092424539", "laboo-room-maroon-snug-black-f20385b8430c.webp"],
    "red-blue/snug": ["10412052382043", "laboo-room-red-blue-snug-black-77cf2a4f5b87.webp"]
  };
  function applyRoomFrameFinishes(config, assetUrl) {
    if (!assetUrl) return;
    const base = new URL(assetUrl, "https://berahgewesjy.shop/");
    for (const room of config.rooms) {
      const correction = corrections[`${config.roomTemplateFamily}/${room.id}`];
      if (correction) room.frameOverlayUrl = new URL(correction[1], base).href;
    }
  }

  // personalisation-product-integration/frame-preview-choices.mjs
  function createFramePreviewChoices(host, { onChange = () => {
  }, followPurchase = false } = {}) {
    host.classList.add("laboo-frame-preview-controls");
    const label = document.createElement("p");
    label.className = "laboo-frame-preview-label";
    label.textContent = "Preview frame colour";
    const choices = document.createElement("div");
    choices.className = "laboo-frame-preview-colours";
    choices.setAttribute("role", "group");
    choices.setAttribute("aria-label", "Preview frame colour");
    const note = document.createElement("p");
    note.className = "laboo-frame-preview-note";
    note.setAttribute("role", "status");
    const buttons = /* @__PURE__ */ new Map();
    let finish = "natural-wood", lastPurchaseFinish = "none", allowed = [];
    for (const [value, name] of FRAME_COLOURS) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.finish = value;
      button.setAttribute("aria-label", "Preview " + name.toLowerCase() + " frame");
      const icon = document.createElement("span");
      icon.className = "laboo-frame-preview-swatch";
      icon.dataset.finish = value;
      icon.setAttribute("aria-hidden", "true");
      const text = document.createElement("span");
      text.textContent = name;
      button.append(icon, text);
      button.onclick = () => {
        if (button.disabled) return;
        finish = value;
        paint2();
        onChange(finish);
      };
      buttons.set(value, button);
      choices.append(button);
    }
    function paint2() {
      for (const [value, button] of buttons) button.setAttribute("aria-pressed", String(value === finish && allowed.includes(value)));
    }
    host.append(label, choices, note);
    return {
      getFinish: () => allowed.includes(finish) ? finish : "none",
      update(model, { getOffer = () => true, requireOffer = false, purchaseFinish = "none", visible = true } = {}) {
        const eligibility = frameEligibility(model);
        allowed = eligibility.eligible ? FRAME_COLOURS.map((c) => c[0]).filter((f) => !requireOffer || !!getOffer(f)) : [];
        if (purchaseFinish !== lastPurchaseFinish && allowed.includes(purchaseFinish)) finish = purchaseFinish;
        lastPurchaseFinish = purchaseFinish;
        if (!allowed.includes(finish)) finish = allowed[0] || "none";
        if (followPurchase) finish = purchaseFinish === "none" || allowed.includes(purchaseFinish) ? purchaseFinish : "none";
        host.hidden = !visible;
        label.hidden = choices.hidden = allowed.length === 0;
        note.textContent = allowed.length ? "Preview colours here. Choose your order\u2019s finish in Size & frame." : eligibility.reason || "Frame not available for this size or destination. Your print is supplied unframed.";
        note.dataset.available = String(allowed.length > 0);
        for (const [value, button] of buttons) {
          button.disabled = !allowed.includes(value);
          button.title = button.disabled ? "This frame finish is currently unavailable." : "";
        }
        paint2();
        return { available: allowed.length > 0, finish: this.getFinish(), reason: allowed.length ? "" : note.textContent };
      }
    };
  }

  // personalisation-product-integration/frame-purchase-rules.mjs
  var FRAME_LABELS = { "natural-wood": "Natural wood", "dark-wood": "Dark wood", black: "Black", white: "White" };
  function lookupFramePurchase(sourceVariantId, finish, country = "GB", purchases = {}) {
    if (!finish || finish === "none") return null;
    if (!Object.hasOwn(FRAME_LABELS, finish)) throw Error("INVALID_FRAME_FINISH");
    const found = Object.values(purchases).filter((p2) => p2.sourceVariantId === String(sourceVariantId) && p2.finish === finish);
    if (found.length !== 1) throw Error("FRAMED_VARIANT_NOT_AVAILABLE");
    const p = found[0];
    if (!/^framed_poster_mounted_/.test(p.productUid) || p.assembly !== "ready-to-hang" || p.country !== "GB") throw Error("INVALID_FRAMED_PRODUCT");
    const regional = regionalFrameProduct(p.mm, finish, country);
    if (!regional) throw Error("FRAMED_VARIANT_NOT_AVAILABLE_FOR_DESTINATION");
    return { ...p, ...regional };
  }

  // personalisation-product-integration/personalised-side-cart.mjs
  async function showPersonalisedSideCart(root2, response) {
    const items = document.getElementById("AjaxCartForm"), subtotal = document.getElementById("AjaxCartSubtotal"), sidebar = document.getElementById("site-cart-sidebar");
    if (!items || !subtotal || typeof sidebar?.show !== "function") return false;
    const text = typeof window.labooCartSectionText === "function" ? await window.labooCartSectionText(response) : await fetch((window.Shopify?.routes?.root || "/") + "?section_id=helper-cart").then((r) => {
      if (!r.ok) throw Error("Cart refresh failed");
      return r.text();
    });
    const section = new DOMParser().parseFromString(text, "text/html"), nextItems = section.getElementById("AjaxCartForm"), nextSubtotal = section.getElementById("AjaxCartSubtotal");
    if (!nextItems || !nextSubtotal) throw Error("Cart refresh failed");
    items.innerHTML = nextItems.innerHTML;
    items.ajaxifyCartItems?.();
    subtotal.innerHTML = nextSubtotal.innerHTML;
    const count = items.querySelector("[data-cart-count]")?.textContent, total = items.querySelector("[data-cart-total]")?.textContent;
    if (count != null) document.querySelectorAll("[data-header-cart-count]").forEach((e) => e.textContent = count);
    if (total != null) document.querySelectorAll("[data-header-cart-total]").forEach((e) => e.textContent = total);
    root2.closest("dialog")?.close();
    sidebar.show();
    document.getElementById("cart-recommendations-sidebar")?.generateRecommendations?.();
    document.getElementById("screen-reader-info")?.replaceChildren(document.createTextNode("Your personalised print was added to your basket."));
    return true;
  }

  // personalisation-product-integration/pilot-red-room-finishes.mjs
  var PILOT_RED_ROOM_FINISHES = { "bedroom": { "none": "laboo-pilot-red-bedroom-none-e3cb2f875ad0.webp", "natural-wood": "laboo-pilot-red-bedroom-natural-wood-7faba736740a.webp", "dark-wood": "laboo-pilot-red-bedroom-dark-wood-66576ab1c5e5.webp", "black": "laboo-pilot-red-bedroom-black-9932c578c5be.webp", "white": "laboo-pilot-red-bedroom-white-34751a2e1968.webp" }, "living-room": { "none": "laboo-pilot-red-living-room-none-1658e432834e.webp", "natural-wood": "laboo-pilot-red-living-room-natural-wood-c9dc19957e17.webp", "dark-wood": "laboo-pilot-red-living-room-dark-wood-6d25ed4c3576.webp", "black": "laboo-pilot-red-living-room-black-853196265806.webp", "white": "laboo-pilot-red-living-room-white-c24825bb0715.webp" }, "gym": { "none": "laboo-pilot-red-gym-none-db698cdb0093.webp", "natural-wood": "laboo-pilot-red-gym-natural-wood-17714005e29b.webp", "dark-wood": "laboo-pilot-red-gym-dark-wood-7686f2e60896.webp", "black": "laboo-pilot-red-gym-black-65c08f81a0f5.webp", "white": "laboo-pilot-red-gym-white-9df73483656f.webp" }, "snug": { "none": "laboo-pilot-red-snug-none-addcaf55bbe1.webp", "natural-wood": "laboo-pilot-red-snug-natural-wood-62e7ef1bcc12.webp", "dark-wood": "laboo-pilot-red-snug-dark-wood-7c866568e992.webp", "black": "laboo-pilot-red-snug-black-a3a61e8cf329.webp", "white": "laboo-pilot-red-snug-white-54012a196e9e.webp" } };

  // personalisation-product-integration/gallery-click-enlarge.js
  document.addEventListener("click", (event) => {
    const surface = event.target.closest("[data-preview-gallery]");
    if (!surface || surface.closest(".laboo-native-map") || event.target.closest("button,a,[data-gallery-index]")) return;
    if (event.target.closest(".laboo-original-art-stage,.laboo-original-paper-stage,.laboo-original-room-stage")) document.querySelector("[data-room-enlarge]")?.click();
  });

  // personalisation-product-integration/shopify-editor.mjs
  var root = document.querySelector("[data-laboo-jersey-fields]");
  if (root) startEditor().catch((e) => {
    root.querySelector("[data-editor-status]").textContent = e.message || "The personaliser could not load. Please reload the page.";
  });
  async function startEditor() {
    const configResponse = await fetch(root.dataset.configUrl);
    if (!configResponse.ok) throw Error("The personaliser could not load. Please reload the page.");
    const config = await configResponse.json();
    root.dataset.productId = String(config.productId);
    let framePurchases = {};
    if (root.dataset.frameMappingUrl) {
      const response = await fetch(root.dataset.frameMappingUrl);
      if (!response.ok) throw Error("Frame options could not load. Please reload the page.");
      framePurchases = await response.json();
      if (Object.values(framePurchases).some((p) => p.productId !== String(config.productId))) throw Error("Frame settings do not match this product.");
    }
    const actualVariants = new Map(JSON.parse(root.querySelector("[data-frame-variant-prices]")?.textContent || "[]").map((v) => [String(v.id), v]));
    function getFrameOffer(source, finish) {
      if (root.dataset.frameCheckout !== "true" || !finish || finish === "none") return null;
      try {
        const p = lookupFramePurchase(source, finish, root.dataset.country || "GB", framePurchases), actual = actualVariants.get(p.purchaseVariantId), print = actualVariants.get(String(source));
        return actual?.available && Number.isInteger(actual.price) && Number.isInteger(print?.price) && actual.price >= print.price ? { ...p, pricePence: actual.price, upgradePence: actual.price - print.price, currency: root.dataset.currency || window.Shopify?.currency?.active || "GBP" } : null;
      } catch {
        return null;
      }
    }
    if (config.template !== root.dataset.template || !Array.isArray(config.rooms) || config.rooms.length !== 4) throw Error("The design settings could not be verified. Please reload the page.");
    applyRoomFrameFinishes(config, root.dataset.roomFrameOverlayUrl);
    if (isPreviewFirstPilot(root) && config.roomTemplateFamily === "red") {
      const assetBase = new URL(root.dataset.frameTextureUrl, location.origin);
      for (const scene of config.rooms) scene.finishOverlayUrls = Object.fromEntries(Object.entries(PILOT_RED_ROOM_FINISHES[scene.id]).map(([finish, file]) => [finish, new URL(file, assetBase).href]));
    }
    root.dataset.exactRoomFinishes = String(config.rooms.every((scene) => scene.finishOverlayUrls));
    const form = root.closest("form"), name = root.querySelector('[name="laboo_name"]'), number2 = root.querySelector('[name="laboo_number"]'), preview = document.querySelector("[data-laboo-jersey-preview]"), status = root.querySelector("[data-editor-status]"), button = form.querySelector('[name="add"]');
    const requestedFrame = framePurchases[new URL(window.location.href).searchParams.get("variant")];
    if (requestedFrame && requestedFrame.productId === String(config.productId)) {
      const source = pageSourceVariant(requestedFrame.sourceVariantId);
      if (source) source.click();
    }
    function pageSourceVariant(id) {
      return [...form.closest("product-page").querySelectorAll(".single-variant")].find((n) => n.dataset.pVariantId === id);
    }
    const variant = () => form.querySelector('[name="id"]')?.value, api = root.dataset.api;
    const paperCanvas = document.querySelector("[data-laboo-paper-preview]");
    const updatePaper = paperCanvas ? createPaperMockup(paperCanvas, root.dataset.paperUrl, document.querySelector("[data-paper-status]"), { onRender: syncLightbox }) : null;
    const roomCanvas = document.querySelector("[data-laboo-room-preview]");
    const lightbox = document.querySelector("[data-room-lightbox]"), lightboxCanvas = lightbox?.querySelector("canvas"), enlargeButton = document.querySelector("[data-room-enlarge]");
    const landingImage = document.querySelector("[data-laboo-landing-image]");
    const cleanFrame = root.dataset.cleanFrame === "true" ? document.createElement("canvas") : null;
    let frameDetailsImage = null;
    if (root.dataset.frameDetailsUrl) {
      const stage = document.createElement("div");
      stage.className = "laboo-original-frame-details-stage";
      frameDetailsImage = new Image();
      frameDetailsImage.crossOrigin = "anonymous";
      frameDetailsImage.width = 1920;
      frameDetailsImage.height = 1920;
      frameDetailsImage.alt = "Close-up of four slim wooden frame finishes: white, natural wood, dark wood and black.";
      frameDetailsImage.src = root.dataset.frameDetailsUrl;
      frameDetailsImage.addEventListener("load", syncLightbox);
      stage.append(frameDetailsImage);
      roomCanvas.closest(".laboo-original-room-stage").after(stage);
      const thumb = document.createElement("button");
      thumb.type = "button";
      thumb.className = "laboo-gallery-thumbnail";
      thumb.dataset.galleryIndex = "6";
      thumb.setAttribute("aria-label", "Show frame finishes and details");
      thumb.setAttribute("aria-pressed", "false");
      const img = frameDetailsImage.cloneNode();
      img.loading = "lazy";
      img.alt = "White, natural wood, dark wood and black frame finishes";
      thumb.append(img);
      document.querySelector(".laboo-gallery-thumbnails").append(thumb);
    }
    let updateCleanFrame;
    const frameLabel = isPreviewFirstPilot(root) ? "ready to hang" : root.dataset.frameReview === "true" && root.dataset.launchMode !== "live" ? "frame optional" : "frame not included";
    if (cleanFrame) {
      cleanFrame.width = 1200;
      cleanFrame.height = 1200;
      cleanFrame.className = "laboo-clean-frame-preview";
      cleanFrame.setAttribute("role", "img");
      cleanFrame.setAttribute("aria-label", `Your personalised artwork in a natural wood frame. Framed example; ${frameLabel}.`);
      cleanFrame.setAttribute("aria-busy", "true");
      preview.after(cleanFrame);
      updateCleanFrame = createCleanFrameMockup(cleanFrame, root.dataset.frameTextureUrl, { frameLabel, onRender() {
        const thumb2 = document.querySelector("[data-clean-frame-thumbnail]");
        if (thumb2) {
          thumb2.getContext("2d").drawImage(cleanFrame, 0, 0, thumb2.width, thumb2.height);
          thumb2.setAttribute("aria-label", cleanFrame.getAttribute("aria-label"));
        }
        syncLightbox();
      } });
      const thumb = document.createElement("canvas");
      thumb.width = 180;
      thumb.height = 180;
      thumb.dataset.cleanFrameThumbnail = "";
      thumb.className = "laboo-clean-frame-thumbnail";
      thumb.setAttribute("aria-label", "Natural wood framed example");
      document.querySelector("[data-print-thumbnail]").after(thumb);
    }
    function syncLightbox() {
      const source = activeView === "frame-details" ? frameDetailsImage : activeView === "print" ? preview.closest(".laboo-desktop-cover") && landingImage ? landingImage : cleanFrame || preview : activeView === "paper" ? paperCanvas : roomCanvas;
      const ready = source?.tagName === "IMG" ? source.complete && source.naturalWidth > 0 : source?.getAttribute("aria-busy") === "false";
      if (enlargeButton) enlargeButton.disabled = !ready;
      if (!lightbox?.open) return;
      lightboxCanvas.setAttribute("aria-busy", String(!ready));
      lightboxCanvas.style.opacity = ready ? "1" : ".35";
      lightbox.querySelector("[data-lightbox-caption]").textContent = document.querySelector("[data-gallery-caption]").textContent;
      if (!ready) return;
      const width = source.naturalWidth || source.width, height = source.naturalHeight || source.height, scale = Math.min(1, 1800 / Math.max(width, height));
      lightboxCanvas.width = Math.round(width * scale);
      lightboxCanvas.height = Math.round(height * scale);
      lightboxCanvas.getContext("2d").drawImage(source, 0, 0, lightboxCanvas.width, lightboxCanvas.height);
      lightboxCanvas.setAttribute("aria-label", `${source.getAttribute("aria-label") || source.alt}, enlarged`);
    }
    const updateRoom = roomCanvas ? createRoomMockups(roomCanvas, document.querySelector("[data-room-status]"), document.querySelector("[data-room-caption]"), { onRender: syncLightbox, rooms: config.rooms }) : null;
    let activeView = "print", roomIndex = 0, galleryIndex = 0, latestPreview;
    let framePreviewChoices = null;
    if (cleanFrame) {
      const host = document.createElement("div");
      document.querySelector("[data-preview-gallery]").after(host);
      framePreviewChoices = createFramePreviewChoices(host, { followPurchase: isPreviewFirstPilot(root), onChange() {
        if (!latestPreview) return;
        renderActive();
        updateCleanFrame?.(latestPreview.url, printProduct(config.variants[variant()], root.dataset.country || "GB").mm, framePreviewChoices.getFinish()).catch(() => {
          status.textContent = "The frame colour preview could not load. Please reload the page.";
        });
      } });
    }
    const galleryLabels = [cleanFrame ? "Framed example \xB7 " + frameLabel : "Your print", ...config.rooms.map((r) => r.label), "Paper mockup"];
    if (frameDetailsImage) galleryLabels.push("Frame finishes & details");
    const galleryCount = galleryLabels.length;
    config.rooms.forEach((room, i) => {
      const thumbnail = document.querySelector(`[data-room-thumbnail="${i}"]`)?.closest("[data-gallery-index]");
      if (thumbnail) {
        thumbnail.dataset.galleryIndex = String(i + 1);
        thumbnail.setAttribute("aria-label", `Show ${room.label.toLowerCase()} preview`);
      }
    });
    const paperThumbnail = document.querySelector("[data-paper-thumbnail]");
    const paperButton = paperThumbnail?.closest("[data-gallery-index]");
    if (paperButton) {
      paperButton.dataset.galleryIndex = "5";
      const detailsButton = paperButton.parentElement.querySelector('[data-gallery-index="6"]');
      if (detailsButton) detailsButton.before(paperButton);
      else paperButton.parentElement.append(paperButton);
    }
    const updatePaperThumbnail = paperThumbnail ? createPaperMockup(paperThumbnail, root.dataset.paperUrl, null, { size: 240, isActive: () => true }) : null;
    preview.addEventListener("load", syncLightbox);
    landingImage?.addEventListener("load", syncLightbox);
    function syncFrameChoices() {
      if (!framePreviewChoices) return;
      const p = values(true), model = printProduct(config.variants[p.variantId], p.country), eligible = frameEligibility({ ...model, country: p.country }).eligible;
      const hasOffer = root.dataset.frameCheckout !== "true" || FRAME_COLOURS.some(([finish]) => !!getFrameOffer(p.variantId, finish));
      const state = framePreviewChoices.update({ ...model, country: p.country }, { getOffer: (finish) => getFrameOffer(p.variantId, finish), requireOffer: root.dataset.frameCheckout === "true", purchaseFinish: p.finish || "none", visible: activeView === "print" || !eligible || !hasOffer });
      galleryLabels[0] = state.finish === "none" ? "Your print \xB7 Print only" : state.available ? "Framed example \xB7 " + FRAME_COLOURS.find((c) => c[0] === state.finish)?.[1] + " \xB7 " + frameLabel : "Your print \xB7 " + state.reason;
    }
    function renderActive() {
      if (!latestPreview) return;
      syncFrameChoices();
      const { url, name: name2, number: number3 } = latestPreview;
      document.querySelector("[data-gallery-caption]").textContent = activeView === "frame-details" ? galleryLabels[galleryIndex] : `${galleryLabels[galleryIndex]} \xB7 ${name2.toUpperCase()} / ${number3}`;
      if (activeView === "paper") updatePaper?.(url, name2, number3);
      if (activeView === "rooms") updateRoom?.(roomIndex, url, name2, number3, isPreviewFirstPilot(root) ? framePreviewChoices?.getFinish() : void 0);
      syncLightbox();
    }
    let sequence = 0, timer, controller, busy = false, objectUrl, lastVariant = variant(), lastPreviewKey = null, mobileFlow;
    let savedProof = null, savedProofKey = null, saveAttempt = null;
    function values(sample = false) {
      return { name: name.value || (sample ? "YOUR NAME" : ""), number: number2.value || (sample ? "10" : ""), variantId: variant(), template: root.dataset.template || "red-white-original-v1", country: root.dataset.country || "GB", revision: PRINT_REVISION, ...mobileFlow?.getSelection() };
    }
    function validate(p) {
      return p.name.trim().length > 0 && p.name.trim().length <= 18 && /^[\p{L} '\-]+$/u.test(p.name) && /\p{L}/u.test(p.name) && /^\d{1,2}$/.test(p.number) && p.variantId;
    }
    function validateFields({ focus = false } = {}) {
      const p = values(), invalid = [name, number2].filter((field) => field === name ? !validate({ ...p, number: "10", variantId: "valid" }) : !/^\d{1,2}$/.test(p.number));
      for (const field of [name, number2]) field.setAttribute("aria-invalid", String(invalid.includes(field)));
      if (focus) invalid[0]?.focus();
      return invalid.length === 0;
    }
    for (const field of [name, number2]) {
      field.addEventListener("blur", () => {
        if (field.value || field.getAttribute("aria-invalid") === "true") validateFields();
      });
      field.addEventListener("input", () => {
        if (field.hasAttribute("aria-invalid")) validateFields();
      });
      field.addEventListener("invalid", () => {
        validateFields({ focus: true });
      });
    }
    function updateButton() {
      button.disabled = busy || !validate(values()) || lastPreviewKey !== JSON.stringify(values(true)) || mobileFlow?.canSubmit() === false;
      button.setAttribute("aria-disabled", String(button.disabled));
    }
    async function draw() {
      const current = ++sequence;
      controller?.abort();
      controller = new AbortController();
      updateButton();
      const p = values(true);
      root.querySelector("[data-name-count]").textContent = `${name.value.length}/18`;
      if (!validate(p)) {
        status.textContent = "Enter a name using letters, spaces, apostrophes or hyphens, and a number from 0 to 99.";
        return;
      }
      preview.setAttribute("aria-busy", "true");
      try {
        const r = await fetch(api + "/preview", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(p), signal: controller.signal });
        if (!r.ok) {
          const d = await r.json();
          throw Error(d.error || "Preview unavailable.");
        }
        const blob = await r.blob();
        if (current !== sequence) return;
        const next = URL.createObjectURL(blob);
        preview.src = next;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = next;
        latestPreview = { url: next, name: p.name, number: p.number };
        renderActive();
        updateCleanFrame?.(next, printProduct(config.variants[p.variantId], p.country).mm, framePreviewChoices?.getFinish() || "none").catch(() => {
          status.textContent = "The framed example could not load. Please reload the page.";
        });
        preview.alt = `Your ${p.name.toUpperCase()} ${p.number} ${config.sport === "basketball" ? "basketball jersey" : "football shirt"} print`;
        mobileFlow?.setPreview(next, preview.alt);
        const printThumbnail = document.querySelector("[data-print-thumbnail]");
        printThumbnail.src = next;
        printThumbnail.alt = `${p.name.toUpperCase()} / ${p.number}, your print thumbnail`;
        updateRoom?.prepareThumbnails(next, p.name, p.number, isPreviewFirstPilot(root) ? framePreviewChoices?.getFinish() : void 0);
        const preparePaper = () => updatePaperThumbnail?.(next, p.name, p.number);
        if ("requestIdleCallback" in window) window.requestIdleCallback(preparePaper, { timeout: 700 });
        else setTimeout(preparePaper, 32);
        status.textContent = "";
        preview.setAttribute("aria-busy", "false");
        lastPreviewKey = JSON.stringify(p);
        updateButton();
        syncLightbox();
        document.querySelector("[data-laboo-preview-caption]").textContent = `${p.name.toUpperCase()} / ${p.number}`;
      } catch (e) {
        if (e.name !== "AbortError" && current === sequence) {
          status.textContent = e.message;
          button.disabled = true;
        }
      }
    }
    const proofSaveKey = () => JSON.stringify([values(), mobileFlow?.getGiftKey() || null]);
    function queue() {
      if (savedProofKey !== proofSaveKey()) {
        savedProof = null;
        mobileFlow?.invalidateProof();
      }
      clearTimeout(timer);
      updateButton();
      timer = setTimeout(draw, 220);
    }
    name.addEventListener("input", queue);
    number2.addEventListener("input", queue);
    root.addEventListener("laboo:finish-change", queue);
    form.addEventListener("change", (event) => {
      if (event.target.matches('[name="id"],[name="laboo_name"],[name="laboo_number"]')) queue();
    });
    const variantInput = form.querySelector('[name="id"]');
    if (variantInput) new MutationObserver(queue).observe(variantInput, { attributes: true, attributeFilter: ["value"] });
    const watch = setInterval(() => {
      if (!root.isConnected) {
        clearInterval(watch);
        return;
      }
      if (lastVariant !== variant()) {
        lastVariant = variant();
        queue();
      }
    }, 350);
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (busy) return;
      if (mobileFlow?.beforeSubmit() === false) return;
      const p = values();
      if (!validateFields({ focus: true }) || !validate(p) || lastPreviewKey !== JSON.stringify(values(true))) {
        status.textContent = "Please enter your name and shirt number, then wait for the preview.";
        return;
      }
      const proof = mobileFlow?.getProof();
      if (!proof || proof !== savedProof || savedProofKey !== proofSaveKey()) {
        status.textContent = "Please check and approve a fresh final proof.";
        return;
      }
      busy = true;
      updateButton();
      status.textContent = "Recording your proof approval\u2026";
      try {
        const ticketToken = new URL(proof.customerProofUrl).pathname.split("/").at(-1);
        const r = await fetch(api + "/approve", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ticketToken, sha256: proof.sha256, confirmed: true }) });
        const approval = await r.json();
        if (!r.ok || !approval.approved || approval.sha256 !== proof.sha256) throw Error(approval.error || "Your proof approval could not be recorded.");
        const saved = proof;
        const properties = { Name: saved.name, Number: saved.number, "Print size": saved.size, _Laboo_Design_ID: saved.designId, _Laboo_Preview_URL: saved.cartPreviewUrl || saved.previewUrl };
        if (saved.purchaseVariantId) Object.assign(properties, { Frame: saved.frameLabel, Assembly: "Ready to hang" });
        if (root.dataset.launchMode !== "live") properties._Laboo_Preview_Only = saved.template || "red-white-original-v1";
        if (saved.customerProofUrl) Object.assign(properties, { _Laboo_Proof_URL: saved.customerProofUrl, _Laboo_Proof_System: "sports-v1", _Laboo_Proof_Stage: "before_checkout" });
        Object.assign(properties, giftOrderProperties(saved.gift));
        const cartRequest = isPreviewFirstPilot(root) && typeof window.labooCartRequest === "function" ? window.labooCartRequest : window.fetch.bind(window);
        const add = await cartRequest((window.Shopify?.routes?.root || "/") + "cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items: [{ id: saved.purchaseVariantId || saved.variantId, quantity: 1, properties }] }) });
        const added = typeof add.json === "function" ? await add.json() : add;
        if (add.ok === false || added.status >= 400) throw Error(added.description || "Shopify could not add this print.");
        if (saved.gift) {
          try {
            await fetch((window.Shopify?.routes?.root || "/") + "cart/update.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ attributes: { "Gift order": "Yes" } }) });
          } catch {
          }
        }
        if (isPreviewFirstPilot(root)) {
          try {
            if (await showPersonalisedSideCart(root, added)) {
              busy = false;
              updateButton();
              return;
            }
          } catch {
          }
        }
        window.location.assign((window.Shopify?.routes?.root || "/") + "cart" + (root.dataset.launchMode === "live" ? "" : "?preview_theme_id=" + root.dataset.themeId));
      } catch (err) {
        status.textContent = err.message;
        busy = false;
        updateButton();
      }
    }, true);
    function selectGallery(index) {
      const previous = galleryIndex;
      galleryIndex = (index + galleryCount) % galleryCount;
      activeView = galleryIndex === 0 ? "print" : galleryIndex === 5 ? "paper" : galleryIndex === 6 ? "frame-details" : "rooms";
      roomIndex = Math.max(0, galleryIndex - 1);
      const frontOnly = !!root.closest("dialog[open]") && root.dataset.exactRoomFinishes === "false";
      if (frontOnly && activeView === "rooms") {
        galleryIndex = index > previous ? 5 : 0;
        activeView = galleryIndex === 0 ? "print" : "paper";
      }
      const page = root.closest("product-page");
      page.classList.toggle("laboo-show-room-examples", activeView === "rooms");
      page.classList.toggle("laboo-show-paper", activeView === "paper");
      page.classList.toggle("laboo-show-frame-details", activeView === "frame-details");
      document.querySelector("[data-preview-gallery]").dataset.selectedImage = String(galleryIndex + 1);
      document.querySelectorAll("[data-gallery-index]").forEach((b) => b.setAttribute("aria-pressed", String(Number(b.dataset.galleryIndex) === galleryIndex)));
      const shown = frontOnly ? [0, 5, ...frameDetailsImage ? [6] : []] : Array.from({ length: galleryCount }, (_, i) => i);
      document.querySelectorAll("[data-gallery-position]").forEach((el) => el.textContent = `${shown.indexOf(galleryIndex) + 1} / ${shown.length}`);
      renderActive();
    }
    document.querySelectorAll("[data-gallery-index]").forEach((b) => b.addEventListener("click", () => selectGallery(Number(b.dataset.galleryIndex))));
    selectGallery(0);
    document.querySelectorAll("[data-gallery-step]").forEach((b) => b.addEventListener("click", () => selectGallery(galleryIndex + Number(b.dataset.galleryStep))));
    for (const surface of [document.querySelector("[data-preview-gallery]"), lightbox?.querySelector(".laboo-room-lightbox-image")].filter(Boolean)) attachSwipeGallery(surface, (direction) => selectGallery(galleryIndex + direction));
    document.querySelectorAll("[data-preview-gallery],[data-room-lightbox]").forEach((g) => g.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        selectGallery(galleryIndex + (e.key === "ArrowLeft" ? -1 : 1));
      }
    }));
    enlargeButton?.addEventListener("click", () => {
      if (enlargeButton.disabled) return;
      lightbox.showModal();
      syncLightbox();
    });
    document.querySelector("[data-landing-gallery-open]")?.addEventListener("click", () => enlargeButton?.click());
    lightbox?.querySelector("[data-room-close]")?.addEventListener("click", () => lightbox.close());
    lightbox?.addEventListener("click", (e) => {
      if (e.target === lightbox) lightbox.close();
    });
    async function prepareProof() {
      const p = values(), gift = mobileFlow?.getGift() || null, key = proofSaveKey();
      if (!validateFields({ focus: true }) || !validate(p) || lastPreviewKey !== JSON.stringify(values(true))) throw Error("Please finish your name, number and size, then wait for the preview.");
      if (savedProof && savedProofKey === key) return savedProof;
      if (saveAttempt?.key !== key) saveAttempt = { key, requestId: crypto.randomUUID() };
      const r = await fetch(api + "/save", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...p, gift, requestId: saveAttempt.requestId }) });
      const saved = await r.json();
      if (!r.ok) throw Error(saved.error || "Your final proof could not be prepared.");
      if (root.dataset.launchMode === "live" && saved.previewOnly) throw Error("Personalisation is temporarily unavailable. Please try again later.");
      if (proofSaveKey() !== key) throw Error("Your design changed. Please prepare a new final proof.");
      savedProof = saved;
      savedProofKey = key;
      return saved;
    }
    mobileFlow = setupMobileLayout(root, { validatePrint: () => validateFields({ focus: true }) && validate(values()) && (!printProduct(config.variants[variant()], root.dataset.country || "GB").available || lastPreviewKey === JSON.stringify(values(true))), prepareProof, onChange: updateButton, onOpen: () => {
      if (root.dataset.exactRoomFinishes === "false") selectGallery(0);
      else renderActive();
    }, variants: config.variants, getFrameOffer, initialFinish: root.dataset.frameCheckout === "true" ? requestedFrame?.finish || "none" : "none" });
    updateButton();
    draw();
  }
})();
